// ABOUTME: Educational module definitions for the learning system
// ABOUTME: Provides structured content, quizzes, and progression for all topics

import type { EducationalModule, Quiz } from '@/types/content'

/**
 * Educational modules organized by level
 * Each module contains lessons, quizzes, and learning objectives
 */
export const EDUCATIONAL_MODULES: EducationalModule[] = [
  // ==========================================
  // LEVEL 1 MODULES - Foundations
  // ==========================================
  {
    id: 'wardrobe-basics-101',
    title: 'Wardrobe Basics 101',
    level: 1,
    category: 'wardrobe',
    estimatedMinutes: 15,
    xpReward: 100,
    prerequisites: [],
    order: 1,
    description: "Learn the fundamental building blocks of a gentleman's wardrobe",
    learningObjectives: [
      'Understand the 5 essential wardrobe categories',
      'Identify foundation pieces every gentleman needs',
      'Learn proper garment care and storage',
      'Recognize quality markers in clothing',
    ],
    content: {
      sections: [
        {
          title: 'Introduction to Wardrobe Categories',
          content:
            'A well-organized wardrobe starts with understanding the five core categories: Tops, Bottoms, Outerwear, Footwear, and Accessories. Each category serves a specific purpose in building complete outfits.',
          media: {
            type: 'image',
            url: '/content/images/wardrobe-categories.jpg',
            alt: 'Five wardrobe categories illustrated',
          },
        },
        {
          title: 'Foundation Pieces',
          content:
            'Every gentleman needs essential foundation pieces: white dress shirt, navy blazer, dark denim jeans, chinos (navy and khaki), white t-shirts, and leather dress shoes. These pieces form the backbone of countless outfit combinations.',
          media: {
            type: 'image',
            url: '/content/images/foundation-pieces.jpg',
            alt: 'Essential foundation garments',
          },
        },
        {
          title: 'Quality Markers',
          content:
            'Learn to identify quality: check stitching density (8-10 stitches per inch), examine fabric weight and hand feel, look for reinforced stress points, and inspect button attachment and material quality.',
          media: {
            type: 'video',
            url: '/content/videos/quality-inspection.mp4',
            alt: 'How to inspect garment quality',
          },
        },
        {
          title: 'Garment Care',
          content:
            'Proper care extends garment life: use quality hangers, rotate shoes regularly, brush suits after wearing, follow care labels, and invest in a steamer for wrinkle removal.',
          media: {
            type: 'image',
            url: '/content/images/garment-care.jpg',
            alt: 'Proper garment storage and care',
          },
        },
      ],
    },
    quizId: 'wardrobe-basics-quiz',
  },

  {
    id: 'color-wheel-introduction',
    title: 'Color Wheel & Color Theory Basics',
    level: 1,
    category: 'color',
    estimatedMinutes: 20,
    xpReward: 120,
    prerequisites: [],
    order: 2,
    description: 'Master the fundamentals of color theory and the color wheel',
    learningObjectives: [
      'Understand primary, secondary, and tertiary colors',
      'Learn color wheel relationships',
      'Identify warm vs cool colors',
      'Apply basic color harmony rules',
    ],
    content: {
      sections: [
        {
          title: 'The Color Wheel',
          content:
            'The color wheel is a visual representation of color relationships. It contains 12 colors: 3 primary (red, blue, yellow), 3 secondary (green, orange, purple), and 6 tertiary colors formed by mixing primary and secondary colors.',
          media: {
            type: 'image',
            url: '/content/images/color-wheel.jpg',
            alt: 'Standard 12-color wheel',
          },
        },
        {
          title: 'Warm vs Cool Colors',
          content:
            'Colors are divided into warm (reds, oranges, yellows) and cool (blues, greens, purples). Warm colors advance and energize, while cool colors recede and calm. Understanding this helps create balanced outfits.',
          media: {
            type: 'interactive',
            url: '/content/interactive/warm-cool-sorter',
            alt: 'Interactive warm/cool color sorting',
          },
        },
        {
          title: 'Color Harmony Rules',
          content:
            'Complementary colors sit opposite on the wheel (red-green, blue-orange). Analogous colors sit adjacent (blue, blue-green, green). Triadic colors form triangles (red, yellow, blue). These relationships create pleasing combinations.',
          media: {
            type: 'image',
            url: '/content/images/color-harmony.jpg',
            alt: 'Color harmony examples',
          },
        },
        {
          title: 'Applying Color Theory',
          content:
            'Start with neutral bases (black, white, gray, navy, beige) and add color strategically. Use the 60-30-10 rule: 60% dominant color, 30% secondary color, 10% accent color.',
          media: {
            type: 'image',
            url: '/content/images/outfit-color-ratio.jpg',
            alt: '60-30-10 rule in outfit',
          },
        },
      ],
    },
    quizId: 'color-wheel-quiz',
  },

  {
    id: 'building-foundation-wardrobe',
    title: 'Building Your Foundation Wardrobe',
    level: 1,
    category: 'wardrobe',
    estimatedMinutes: 20,
    xpReward: 130,
    prerequisites: ['wardrobe-basics-101'],
    order: 3,
    description: 'Create a versatile foundation wardrobe that works for any occasion',
    learningObjectives: [
      'Identify the 15 essential items every man needs',
      'Understand versatility and cost-per-wear',
      'Plan strategic wardrobe investments',
      'Build outfit combinations from basics',
    ],
    content: {
      sections: [
        {
          title: 'The Essential 15',
          content:
            'Your foundation wardrobe should include: 2 white dress shirts, 1 light blue dress shirt, 1 navy blazer, 1 gray suit, 2 pairs of chinos (navy, khaki), 1 pair dark jeans, white and gray t-shirts, 1 navy sweater, brown and black leather shoes, and a quality leather belt.',
          media: {
            type: 'image',
            url: '/content/images/essential-15.jpg',
            alt: 'The 15 essential wardrobe items',
          },
        },
        {
          title: 'Cost Per Wear',
          content:
            'Quality over quantity: A $200 blazer worn 100 times costs $2 per wear. A $50 trendy jacket worn 5 times costs $10 per wear. Invest in timeless pieces you will wear repeatedly.',
          media: {
            type: 'image',
            url: '/content/images/cost-per-wear.jpg',
            alt: 'Cost per wear calculation example',
          },
        },
        {
          title: 'Versatility Matrix',
          content:
            'Each foundation piece should work with at least 5 other items. A navy blazer pairs with: gray trousers, chinos, jeans, dress pants, and shorts. This creates exponential outfit combinations.',
          media: {
            type: 'image',
            url: '/content/images/versatility-matrix.jpg',
            alt: 'Outfit combination possibilities',
          },
        },
        {
          title: 'Investment Strategy',
          content:
            'Prioritize spending on: shoes (most visible, longest lasting), outerwear (worn daily in season), suits (key for professional settings). Save on: t-shirts, casual basics, trendy items.',
          media: {
            type: 'image',
            url: '/content/images/investment-pyramid.jpg',
            alt: 'Where to invest in your wardrobe',
          },
        },
      ],
    },
    quizId: 'foundation-wardrobe-quiz',
  },

  {
    id: 'closet-organization',
    title: 'Closet Organization Mastery',
    level: 1,
    category: 'wardrobe',
    estimatedMinutes: 15,
    xpReward: 100,
    prerequisites: [],
    order: 4,
    description: 'Transform your closet into an efficient style command center',
    learningObjectives: [
      'Organize clothing by category and color',
      'Implement proper storage techniques',
      'Conduct seasonal wardrobe audits',
      'Maintain an organized system long-term',
    ],
    content: {
      sections: [
        {
          title: 'Category Organization',
          content:
            'Organize by type first: suits together, shirts together, pants together. Within each category, arrange by color from light to dark. This makes outfit selection intuitive and fast.',
          media: {
            type: 'image',
            url: '/content/images/closet-categories.jpg',
            alt: 'Closet organized by category',
          },
        },
        {
          title: 'Proper Hanging',
          content:
            'Use wooden hangers for suits and blazers (maintains shape). Velvet hangers for shirts (prevents slipping). Never hang sweaters (causes stretching). Fold knits and place on shelves.',
          media: {
            type: 'image',
            url: '/content/images/hanger-types.jpg',
            alt: 'Different hanger types for different garments',
          },
        },
        {
          title: 'Seasonal Rotation',
          content:
            'Store off-season items in breathable garment bags. Rotate seasonally: spring/summer items accessible March-August, fall/winter September-February. This maximizes closet space.',
          media: {
            type: 'image',
            url: '/content/images/seasonal-rotation.jpg',
            alt: 'Seasonal wardrobe rotation system',
          },
        },
        {
          title: 'The One-Year Rule',
          content:
            "If you haven't worn something in 12 months, consider donating it. Exceptions: formal wear, seasonal specialty items. Regular audits prevent closet clutter and decision fatigue.",
          media: {
            type: 'image',
            url: '/content/images/wardrobe-audit.jpg',
            alt: 'Conducting a wardrobe audit',
          },
        },
      ],
    },
    quizId: 'closet-organization-quiz',
  },

  // ==========================================
  // LEVEL 2 MODULES - Color & Style
  // ==========================================
  {
    id: 'seasonal-color-analysis',
    title: 'Seasonal Color Analysis',
    level: 2,
    category: 'color',
    estimatedMinutes: 25,
    xpReward: 150,
    prerequisites: ['color-wheel-introduction'],
    order: 3,
    description: 'Discover your personal color season and optimal palette',
    learningObjectives: [
      'Determine your color season (Spring, Summer, Autumn, Winter)',
      'Understand undertones (warm, cool, neutral)',
      'Identify colors that enhance your natural coloring',
      'Build a cohesive personal color palette',
    ],
    content: {
      sections: [
        {
          title: 'Understanding Color Seasons',
          content:
            'The four season system categorizes individuals based on natural coloring. Spring: warm, bright. Summer: cool, muted. Autumn: warm, muted. Winter: cool, bright. Your season determines which colors harmonize with your skin tone, hair, and eyes.',
          media: {
            type: 'image',
            url: '/content/images/four-seasons.jpg',
            alt: 'Four color season palettes',
          },
        },
        {
          title: 'Identifying Your Undertone',
          content:
            'Check your wrist veins: greenish = warm, bluish = cool, both = neutral. Test silver vs gold jewelry against your skin. Consider how you tan: golden = warm, pinkish = cool.',
          media: {
            type: 'interactive',
            url: '/content/interactive/undertone-quiz',
            alt: 'Interactive undertone assessment',
          },
        },
        {
          title: 'Season-Specific Palettes',
          content:
            'Spring: coral, peach, warm greens, golden yellows. Summer: powder blue, lavender, rose pink, soft grays. Autumn: terracotta, olive, rust, camel. Winter: true red, emerald, royal blue, pure white.',
          media: {
            type: 'image',
            url: '/content/images/season-palettes.jpg',
            alt: 'Detailed season color swatches',
          },
        },
        {
          title: 'Building Your Palette',
          content:
            'Select 8-12 colors from your season that you love. Include neutrals, core colors, and accents. Create a digital or physical swatch card to reference when shopping.',
          media: {
            type: 'video',
            url: '/content/videos/build-palette.mp4',
            alt: 'How to create your personal palette',
          },
        },
      ],
    },
    quizId: 'seasonal-color-quiz',
  },

  {
    id: 'monochromatic-styling',
    title: 'Monochromatic & Tonal Styling',
    level: 2,
    category: 'style',
    estimatedMinutes: 18,
    xpReward: 130,
    prerequisites: ['color-wheel-introduction'],
    order: 4,
    description: 'Create sophisticated single-color and tonal outfits',
    learningObjectives: [
      'Understand monochromatic vs tonal dressing',
      'Layer different shades effectively',
      'Add visual interest through texture',
      'Avoid common monochrome mistakes',
    ],
    content: {
      sections: [
        {
          title: 'Monochrome vs Tonal',
          content:
            'Monochromatic uses one color in various shades (navy suit, light blue shirt, navy tie). Tonal uses adjacent colors (navy, cobalt, sky blue). Both create elegant, elongating silhouettes.',
          media: {
            type: 'image',
            url: '/content/images/mono-vs-tonal.jpg',
            alt: 'Monochromatic vs tonal outfits',
          },
        },
        {
          title: 'Texture Mixing',
          content:
            'Prevent monotony by varying textures: combine matte and shiny, smooth and textured. Pair wool trousers with cotton shirt and silk tie, all in the same color family.',
          media: {
            type: 'image',
            url: '/content/images/texture-variety.jpg',
            alt: 'Same color, different textures',
          },
        },
        {
          title: 'Common Mistakes',
          content:
            "Avoid exact color matching (shirt and tie shouldn't be identical). Don't neglect contrast (add lighter or darker piece). Include accessories (watch, belt, shoes) in complementary metals or leather tones.",
          media: {
            type: 'video',
            url: '/content/videos/mono-mistakes.mp4',
            alt: 'Monochrome styling pitfalls',
          },
        },
      ],
    },
    quizId: 'monochromatic-quiz',
  },

  {
    id: 'color-theory-fundamentals',
    title: 'Color Theory Fundamentals',
    level: 2,
    category: 'color',
    estimatedMinutes: 22,
    xpReward: 140,
    prerequisites: ['color-wheel-introduction'],
    order: 7,
    description: 'Deep dive into advanced color theory concepts',
    learningObjectives: [
      'Understand hue, saturation, and value',
      'Master color temperature variations',
      'Apply contrast principles to outfits',
      'Create sophisticated color schemes',
    ],
    content: {
      sections: [
        {
          title: 'Hue, Saturation, Value',
          content:
            'Hue is the pure color (red, blue, green). Saturation is intensity (vivid vs muted). Value is lightness/darkness. Understanding these helps you match colors beyond basic names.',
          media: {
            type: 'image',
            url: '/content/images/hsv-diagram.jpg',
            alt: 'Hue, saturation, value explained',
          },
        },
        {
          title: 'Color Temperature',
          content:
            'Every color has warm and cool variants. Cool navy vs warm navy. Cool gray vs warm taupe. Matching temperatures within an outfit creates cohesion.',
          media: {
            type: 'image',
            url: '/content/images/color-temperature.jpg',
            alt: 'Warm vs cool color variants',
          },
        },
        {
          title: 'Contrast Principles',
          content:
            'High contrast (white shirt, black tie) is bold and formal. Low contrast (cream shirt, tan tie) is subtle and relaxed. Medium contrast suits most occasions.',
          media: {
            type: 'image',
            url: '/content/images/contrast-levels.jpg',
            alt: 'High, medium, and low contrast outfits',
          },
        },
      ],
    },
    quizId: 'color-theory-quiz',
  },

  {
    id: 'personal-color-analysis',
    title: 'Personal Color Analysis',
    level: 2,
    category: 'color',
    estimatedMinutes: 25,
    xpReward: 160,
    prerequisites: ['seasonal-color-analysis'],
    order: 8,
    description: 'Determine your unique color profile and best shades',
    learningObjectives: [
      'Assess your natural coloring objectively',
      'Identify your best and worst colors',
      'Build a personalized color palette',
      'Shop confidently using your palette',
    ],
    content: {
      sections: [
        {
          title: 'Self-Assessment Methods',
          content:
            'Use natural daylight for assessment. Note your skin, hair, and eye colors. Try draping different colored fabrics near your face. Notice which colors make you look healthy vs washed out.',
          media: {
            type: 'image',
            url: '/content/images/color-draping.jpg',
            alt: 'Color draping assessment technique',
          },
        },
        {
          title: 'Your Best Colors',
          content:
            'Your best colors will make your skin look clear, eyes bright, and reduce shadows. Wrong colors emphasize dark circles, make skin look sallow, and create an overall tired appearance.',
          media: {
            type: 'image',
            url: '/content/images/good-vs-bad-colors.jpg',
            alt: 'Effects of right vs wrong colors',
          },
        },
        {
          title: 'Building Your Palette',
          content:
            'Select 12-15 colors: 5-6 neutrals (your best blacks, grays, browns), 4-5 core colors (navy, burgundy, forest green), 3-4 accent colors (for ties, pocket squares).',
          media: {
            type: 'image',
            url: '/content/images/personal-palette-builder.jpg',
            alt: 'Personal color palette template',
          },
        },
      ],
    },
    quizId: 'personal-color-quiz',
  },

  {
    id: 'color-combinations',
    title: 'Color Combinations & Schemes',
    level: 2,
    category: 'color',
    estimatedMinutes: 20,
    xpReward: 135,
    prerequisites: ['color-wheel-introduction'],
    order: 9,
    description: 'Master the art of combining colors in outfits',
    learningObjectives: [
      'Apply complementary color schemes',
      'Use analogous colors effectively',
      'Create triadic color combinations',
      'Balance neutral and bold colors',
    ],
    content: {
      sections: [
        {
          title: 'Complementary Combinations',
          content:
            'Opposite colors on the wheel: blue-orange, red-green, yellow-purple. Use 70-30 ratio for balance. Example: navy suit with rust tie, or gray suit with burgundy accessories.',
          media: {
            type: 'image',
            url: '/content/images/complementary-outfits.jpg',
            alt: 'Complementary color outfit examples',
          },
        },
        {
          title: 'Analogous Harmony',
          content:
            'Adjacent colors on the wheel: blue, teal, green. Creates cohesive, sophisticated looks. Lower contrast, more subtle. Example: navy suit, teal tie, green pocket square.',
          media: {
            type: 'image',
            url: '/content/images/analogous-outfits.jpg',
            alt: 'Analogous color outfit examples',
          },
        },
        {
          title: 'Neutral Foundation',
          content:
            'Build outfits on neutral base (gray, navy, charcoal). Add one or two colors as accents. This approach is foolproof and always stylish.',
          media: {
            type: 'image',
            url: '/content/images/neutral-plus-accent.jpg',
            alt: 'Neutral base with color accents',
          },
        },
      ],
    },
    quizId: 'color-combinations-quiz',
  },

  {
    id: 'seasonal-color-palettes',
    title: 'Seasonal Color Palettes',
    level: 2,
    category: 'color',
    estimatedMinutes: 18,
    xpReward: 125,
    prerequisites: ['seasonal-color-analysis'],
    order: 10,
    description: 'Deep dive into each season palette with specific color recommendations',
    learningObjectives: [
      'Master the Spring palette characteristics',
      'Understand Summer palette nuances',
      'Apply Autumn palette effectively',
      'Work with Winter palette colors',
    ],
    content: {
      sections: [
        {
          title: 'Spring Palette',
          content:
            'Clear, warm, bright colors: coral, peach, golden yellow, warm turquoise, salmon, camel. Avoid: dusty, muted, or cool colors. Best metals: gold, brass, rose gold.',
          media: {
            type: 'image',
            url: '/content/images/spring-palette.jpg',
            alt: 'Complete Spring color palette',
          },
        },
        {
          title: 'Summer Palette',
          content:
            'Cool, soft, muted colors: powder blue, lavender, rose pink, soft gray, dusty rose, mauve. Avoid: warm, bright, or harsh colors. Best metals: silver, platinum, white gold.',
          media: {
            type: 'image',
            url: '/content/images/summer-palette.jpg',
            alt: 'Complete Summer color palette',
          },
        },
        {
          title: 'Autumn Palette',
          content:
            'Warm, muted, earthy colors: terracotta, olive, rust, camel, forest green, burgundy. Avoid: cool, bright, or pastel colors. Best metals: gold, bronze, copper.',
          media: {
            type: 'image',
            url: '/content/images/autumn-palette.jpg',
            alt: 'Complete Autumn color palette',
          },
        },
        {
          title: 'Winter Palette',
          content:
            'Cool, clear, vivid colors: true red, emerald, royal blue, pure white, black, fuchsia. Avoid: muted, warm, or earthy colors. Best metals: silver, platinum, white gold.',
          media: {
            type: 'image',
            url: '/content/images/winter-palette.jpg',
            alt: 'Complete Winter color palette',
          },
        },
      ],
    },
    quizId: 'seasonal-palettes-quiz',
  },

  {
    id: 'color-psychology',
    title: 'Color Psychology in Fashion',
    level: 2,
    category: 'color',
    estimatedMinutes: 15,
    xpReward: 110,
    prerequisites: ['color-wheel-introduction'],
    order: 11,
    description: 'Understand the psychological impact of colors in your wardrobe',
    learningObjectives: [
      'Know what colors communicate to others',
      'Choose colors strategically for occasions',
      'Understand cultural color meanings',
      'Use color to influence perception',
    ],
    content: {
      sections: [
        {
          title: 'Color Messages',
          content:
            'Blue: trust, competence, calm. Red: power, energy, passion. Gray: sophistication, neutrality. Black: authority, elegance. White: cleanliness, simplicity. Navy: reliability, professionalism.',
          media: {
            type: 'image',
            url: '/content/images/color-psychology-chart.jpg',
            alt: 'Color psychology meanings',
          },
        },
        {
          title: 'Strategic Color Choice',
          content:
            'Job interview: navy suit (trustworthy). Important meeting: charcoal (authoritative). Networking event: burgundy tie (memorable). Date: blue shirt (approachable).',
          media: {
            type: 'image',
            url: '/content/images/occasion-colors.jpg',
            alt: 'Colors for different occasions',
          },
        },
        {
          title: 'Cultural Considerations',
          content:
            'White means mourning in some Asian cultures. Red is lucky in China. Black is formal in Western cultures but can mean mourning. Research colors when traveling internationally.',
          media: {
            type: 'image',
            url: '/content/images/cultural-colors.jpg',
            alt: 'Cultural color meanings around the world',
          },
        },
      ],
    },
    quizId: 'color-psychology-quiz',
  },

  // ==========================================
  // LEVEL 3 MODULES - Accessories & Details
  // ==========================================
  {
    id: 'tie-knots-mastery',
    title: 'Tie Knots & Neckwear',
    level: 3,
    category: 'accessories',
    estimatedMinutes: 30,
    xpReward: 180,
    prerequisites: ['wardrobe-basics-101'],
    order: 5,
    description: 'Master essential tie knots and neckwear selection',
    learningObjectives: [
      'Tie four essential knots (Four-in-Hand, Half-Windsor, Full Windsor, Pratt)',
      'Choose appropriate knot for collar type',
      'Select tie width for body type',
      'Coordinate tie patterns with shirts and suits',
    ],
    content: {
      sections: [
        {
          title: 'The Four Essential Knots',
          content:
            'Four-in-Hand: asymmetric, slim, everyday. Half-Windsor: medium, symmetric, business. Full Windsor: large, triangular, formal. Pratt: medium, symmetric, versatile. Each suits different collar spreads.',
          media: {
            type: 'video',
            url: '/content/videos/four-knots.mp4',
            alt: 'Step-by-step tie knots tutorial',
          },
        },
        {
          title: 'Collar and Knot Pairing',
          content:
            'Point collar: Four-in-Hand or Pratt. Spread collar: Half-Windsor or Full Windsor. Cutaway collar: Full Windsor. Button-down: Four-in-Hand only. Match knot size to collar spread.',
          media: {
            type: 'interactive',
            url: '/content/interactive/collar-knot-match',
            alt: 'Interactive collar-knot pairing',
          },
        },
        {
          title: 'Pattern Coordination',
          content:
            'Mix pattern scales: large stripe shirt with small dot tie. Match one color between shirt and tie. Solid shirts work with any tie. With patterned suits, keep shirt or tie solid.',
          media: {
            type: 'image',
            url: '/content/images/tie-pattern-combos.jpg',
            alt: 'Successful pattern combinations',
          },
        },
        {
          title: 'Width and Proportion',
          content:
            'Tie width should match lapel width (typically 2.5-3.5 inches). Slim builds: 2.5-3 inches. Average builds: 3-3.25 inches. Larger builds: 3.25-3.75 inches. Tie tip should reach belt buckle.',
          media: {
            type: 'image',
            url: '/content/images/tie-proportions.jpg',
            alt: 'Tie width and length guidelines',
          },
        },
      ],
    },
    quizId: 'tie-knots-quiz',
  },

  {
    id: 'metal-matching-guide',
    title: 'Metal Matching & Accessories',
    level: 3,
    category: 'accessories',
    estimatedMinutes: 15,
    xpReward: 120,
    prerequisites: [],
    order: 6,
    description: 'Coordinate metal accessories for polished looks',
    learningObjectives: [
      'Understand traditional metal matching rules',
      'Learn when to mix metals appropriately',
      'Coordinate watch, belt buckle, and jewelry',
      'Match metals to skin undertone',
    ],
    content: {
      sections: [
        {
          title: 'Traditional Metal Matching',
          content:
            'Classic rule: match all visible metals (watch, belt buckle, cufflinks, tie bar, rings). Silver/platinum for cool undertones. Gold/rose gold for warm undertones. This creates cohesive, polished looks.',
          media: {
            type: 'image',
            url: '/content/images/matched-metals.jpg',
            alt: 'All-silver vs all-gold accessories',
          },
        },
        {
          title: 'Modern Metal Mixing',
          content:
            'Modern approach: mix metals intentionally with one dominant (60%) and one accent (40%). Two-tone watches bridge both. Keep jewelry in one metal family, accessories in another.',
          media: {
            type: 'image',
            url: '/content/images/mixed-metals.jpg',
            alt: 'Tasteful mixed metal combinations',
          },
        },
        {
          title: 'Undertone Coordination',
          content:
            'Warm undertones: gold, bronze, copper, warm brass. Cool undertones: silver, platinum, white gold, chrome. Neutral undertones: any metal or two-tone pieces.',
          media: {
            type: 'interactive',
            url: '/content/interactive/metal-undertone-match',
            alt: 'Interactive metal selection tool',
          },
        },
      ],
    },
    quizId: 'metal-matching-quiz',
  },

  {
    id: 'accessory-fundamentals',
    title: 'Accessory Fundamentals',
    level: 3,
    category: 'accessories',
    estimatedMinutes: 20,
    xpReward: 140,
    prerequisites: [],
    order: 14,
    description: 'Master the basics of accessorizing like a gentleman',
    learningObjectives: [
      'Understand the role of accessories in an outfit',
      'Learn the key accessory categories',
      'Apply the rule of three accessories',
      'Balance accessories with outfit formality',
    ],
    content: {
      sections: [
        {
          title: 'Why Accessories Matter',
          content:
            'Accessories complete an outfit and express personality. They transform basics into polished looks. A simple navy suit becomes distinctive with the right tie, pocket square, and watch.',
          media: {
            type: 'image',
            url: '/content/images/accessories-transform.jpg',
            alt: 'Same suit with different accessories',
          },
        },
        {
          title: 'Essential Categories',
          content:
            'Key accessory categories: neckwear (ties, bow ties), pocket squares, watches, belts, cufflinks, tie bars, lapel pins, socks, and bags. Start with the essentials: quality tie, matching belt, classic watch.',
          media: {
            type: 'image',
            url: '/content/images/accessory-categories.jpg',
            alt: 'Essential accessory categories',
          },
        },
        {
          title: 'The Rule of Three',
          content:
            'Limit statement accessories to three per outfit. More feels cluttered. Examples: tie + watch + pocket square, or cufflinks + tie bar + lapel pin. Let one be the focal point.',
          media: {
            type: 'image',
            url: '/content/images/rule-of-three.jpg',
            alt: 'Rule of three accessories examples',
          },
        },
        {
          title: 'Formality Balance',
          content:
            'Match accessory formality to outfit. Formal: silk tie, metal cufflinks, dress watch. Casual: knit tie or no tie, leather strap watch, minimal jewelry. Never over-accessorize casual outfits.',
          media: {
            type: 'image',
            url: '/content/images/formality-accessories.jpg',
            alt: 'Accessories by formality level',
          },
        },
      ],
    },
    quizId: 'accessory-fundamentals-quiz',
  },

  {
    id: 'tie-styles-guide',
    title: 'Complete Tie Styles Guide',
    level: 3,
    category: 'accessories',
    estimatedMinutes: 22,
    xpReward: 150,
    prerequisites: ['tie-knots-mastery'],
    order: 15,
    description: 'Explore all tie styles, patterns, and when to wear each',
    learningObjectives: [
      'Identify different tie types and materials',
      'Match tie patterns to occasions',
      'Coordinate ties with shirt patterns',
      'Build a versatile tie collection',
    ],
    content: {
      sections: [
        {
          title: 'Tie Types',
          content:
            'Standard necktie: versatile, formal to business casual. Bow tie: formal events, creative professions. Knit tie: textured, smart casual. Skinny tie: modern, slim fits. Wide tie: classic, traditional fits.',
          media: {
            type: 'image',
            url: '/content/images/tie-types.jpg',
            alt: 'Different tie types comparison',
          },
        },
        {
          title: 'Tie Materials',
          content:
            'Silk: smooth, lustrous, most formal. Wool: textured, fall/winter. Cotton: matte, casual. Linen: summer, relaxed. Knit: textured, year-round casual. Choose material based on season and formality.',
          media: {
            type: 'image',
            url: '/content/images/tie-materials.jpg',
            alt: 'Tie materials and textures',
          },
        },
        {
          title: 'Pattern Guide',
          content:
            'Solid: most versatile, always appropriate. Stripes: classic, professional. Dots: playful yet professional. Paisley: bold, statement piece. Geometric: modern, creative. Start with solids, add patterns gradually.',
          media: {
            type: 'image',
            url: '/content/images/tie-patterns.jpg',
            alt: 'Common tie patterns',
          },
        },
        {
          title: 'Building Your Collection',
          content:
            'Start with: navy solid, burgundy solid, navy stripe. Add: gray, forest green, subtle pattern. Eventually: seasonal colors, bold patterns, specialty ties. Quality over quantity.',
          media: {
            type: 'image',
            url: '/content/images/tie-collection.jpg',
            alt: 'Building a tie collection',
          },
        },
      ],
    },
    quizId: 'tie-styles-quiz',
  },

  {
    id: 'watch-basics',
    title: 'Watch Selection & Styling',
    level: 3,
    category: 'accessories',
    estimatedMinutes: 25,
    xpReward: 160,
    prerequisites: [],
    order: 16,
    description: 'Learn to choose and style watches for every occasion',
    learningObjectives: [
      'Understand watch categories and their purposes',
      'Match watches to outfits and occasions',
      'Identify quality watch markers',
      'Build a functional watch collection',
    ],
    content: {
      sections: [
        {
          title: 'Watch Categories',
          content:
            'Dress watch: thin, simple dial, leather strap. Diver: water resistant, rotating bezel. Chronograph: stopwatch function, sporty. Field watch: rugged, military heritage. Each serves different purposes.',
          media: {
            type: 'image',
            url: '/content/images/watch-categories.jpg',
            alt: 'Watch category examples',
          },
        },
        {
          title: 'Matching to Occasions',
          content:
            'Formal events: dress watch with leather strap. Business: dress watch or subtle chronograph. Casual: diver, field, or sports watch. Active: sports watch or fitness tracker. Match formality always.',
          media: {
            type: 'image',
            url: '/content/images/watch-occasions.jpg',
            alt: 'Watches for different occasions',
          },
        },
        {
          title: 'Quality Indicators',
          content:
            'Movement type: automatic (self-winding), quartz (battery). Crystal: sapphire (scratch resistant), mineral (affordable). Water resistance: 50m minimum for daily wear. Brand reputation matters.',
          media: {
            type: 'image',
            url: '/content/images/watch-quality.jpg',
            alt: 'Watch quality indicators',
          },
        },
        {
          title: 'The Three-Watch Collection',
          content:
            'One dress watch (leather strap, clean dial), one versatile daily (steel bracelet diver or chronograph), one casual/sport (rubber or NATO strap). This covers 95% of situations.',
          media: {
            type: 'image',
            url: '/content/images/three-watch-collection.jpg',
            alt: 'Essential three-watch collection',
          },
        },
      ],
    },
    quizId: 'watch-pairing-quiz',
  },

  {
    id: 'belt-shoe-coordination',
    title: 'Belt & Shoe Coordination',
    level: 3,
    category: 'accessories',
    estimatedMinutes: 15,
    xpReward: 120,
    prerequisites: [],
    order: 17,
    description: 'Master the essential rules of belt and shoe matching',
    learningObjectives: [
      'Apply the belt-shoe matching rule',
      'Understand leather types and finishes',
      'Know when rules can be bent',
      'Build a coordinated leather collection',
    ],
    content: {
      sections: [
        {
          title: 'The Matching Rule',
          content:
            'Classic rule: belt and shoes should match in color. Brown with brown, black with black. In formal settings, this rule is strict. In casual settings, it can be relaxed.',
          media: {
            type: 'image',
            url: '/content/images/belt-shoe-match.jpg',
            alt: 'Matching belts and shoes',
          },
        },
        {
          title: 'Leather Tones',
          content:
            'Black: most formal, evening wear. Dark brown: versatile, business to casual. Tan/cognac: casual, warm weather. Burgundy: statement, fashion-forward. Start with black and dark brown.',
          media: {
            type: 'image',
            url: '/content/images/leather-tones.jpg',
            alt: 'Leather color spectrum',
          },
        },
        {
          title: 'Texture Matching',
          content:
            'Match texture too, not just color. Smooth leather belt with smooth leather shoes. Suede belt with suede shoes. Pebbled leather can work with smooth. Consistency creates polish.',
          media: {
            type: 'image',
            url: '/content/images/leather-textures.jpg',
            alt: 'Matching leather textures',
          },
        },
        {
          title: 'Building Your Collection',
          content:
            'Essentials: black dress belt + black dress shoes, dark brown belt + dark brown shoes. Add: tan casual belt + tan loafers, reversible belts for travel efficiency.',
          media: {
            type: 'image',
            url: '/content/images/leather-collection.jpg',
            alt: 'Essential leather accessories collection',
          },
        },
      ],
    },
    quizId: 'belt-shoe-quiz',
  },

  {
    id: 'pocket-square-folding',
    title: 'Pocket Square Mastery',
    level: 3,
    category: 'accessories',
    estimatedMinutes: 18,
    xpReward: 130,
    prerequisites: ['accessory-fundamentals'],
    order: 18,
    description: 'Learn pocket square selection, folding, and styling',
    learningObjectives: [
      'Master essential pocket square folds',
      'Coordinate pocket squares with ties',
      'Choose appropriate fabrics and patterns',
      'Know when to wear pocket squares',
    ],
    content: {
      sections: [
        {
          title: 'Essential Folds',
          content:
            'Presidential (flat): most formal, clean rectangle. TV fold: similar, slightly more height. Puff: casual elegance, relaxed shape. One-point: classic, triangular. Two-point: variation of one-point.',
          media: {
            type: 'video',
            url: '/content/videos/pocket-square-folds.mp4',
            alt: 'How to fold pocket squares',
          },
        },
        {
          title: 'Coordinating with Ties',
          content:
            'Never match exactly (looks like a set). Complement: pick a secondary color from your tie. Contrast: choose a color that harmonizes but differs. White linen works with everything.',
          media: {
            type: 'image',
            url: '/content/images/pocket-square-tie.jpg',
            alt: 'Pocket square and tie coordination',
          },
        },
        {
          title: 'Fabric Selection',
          content:
            'White linen: most versatile, all formal occasions. Silk: lustrous, adds luxury. Cotton: casual, relaxed. Wool: textured, fall/winter. Start with white linen, then add colored silk.',
          media: {
            type: 'image',
            url: '/content/images/pocket-square-fabrics.jpg',
            alt: 'Pocket square fabric options',
          },
        },
        {
          title: 'When to Wear',
          content:
            'Always appropriate with suits and blazers. Optional with casual jackets. Skip with ultra-casual outfits. White linen for interviews and formal events. More expressive for social occasions.',
          media: {
            type: 'image',
            url: '/content/images/pocket-square-occasions.jpg',
            alt: 'Pocket square occasions guide',
          },
        },
      ],
    },
    quizId: 'pocket-square-quiz',
  },

  // ==========================================
  // LEVEL 4 MODULES - Wardrobe Strategy
  // ==========================================
  {
    id: 'capsule-wardrobe-principles',
    title: 'Capsule Wardrobe Principles',
    level: 4,
    category: 'wardrobe',
    estimatedMinutes: 25,
    xpReward: 180,
    prerequisites: ['building-foundation-wardrobe'],
    order: 19,
    description: 'Learn to build a minimal, versatile wardrobe that maximizes outfit combinations',
    learningObjectives: [
      'Understand the philosophy behind capsule wardrobes',
      'Calculate the optimal number of items per category',
      'Apply the interchangeability principle to maximize outfits',
      'Design a capsule wardrobe tailored to your lifestyle',
    ],
    content: {
      sections: [
        {
          title: 'The Capsule Philosophy',
          content:
            'A capsule wardrobe is a curated collection of 25-40 interchangeable pieces that cover all your daily needs. The concept, popularized by Susie Faux in the 1970s, focuses on quality over quantity. Every item must pair with at least 3 others, creating exponential outfit possibilities from fewer pieces.',
          media: {
            type: 'image',
            url: '/content/images/capsule-philosophy.jpg',
            alt: 'Capsule wardrobe concept overview',
          },
        },
        {
          title: 'Category Ratios',
          content:
            'A balanced capsule follows rough ratios: 30% tops (shirts, tees, sweaters), 25% bottoms (trousers, jeans, shorts), 20% outerwear (jackets, blazers, coats), 15% footwear, 10% accessories. Adjust based on your climate and lifestyle. A warm-climate capsule skews toward lighter tops and fewer outerwear pieces.',
          media: {
            type: 'image',
            url: '/content/images/capsule-ratios.jpg',
            alt: 'Category ratio breakdown for capsule wardrobe',
          },
        },
        {
          title: 'The Interchangeability Test',
          content:
            'Before adding any piece, test it against your existing items. Each piece should pair with at least 3 bottoms or 3 tops. A navy blazer passes easily (works with chinos, jeans, trousers). A bright paisley shirt may only work with dark neutrals, limiting its value. Apply this test ruthlessly.',
          media: {
            type: 'interactive',
            url: '/content/interactive/interchangeability-test',
            alt: 'Interactive outfit combination calculator',
          },
        },
        {
          title: 'Building Your First Capsule',
          content:
            'Start with a neutral color base: 2-3 colors for bottoms (navy, gray, khaki), 2-3 for outerwear (navy, charcoal, camel). Add 3-4 accent colors for tops and accessories. Plan for your top 3 activities (work, casual, evening). Aim for 30 items total for your first capsule and refine over 2-3 seasons.',
          media: {
            type: 'image',
            url: '/content/images/first-capsule-plan.jpg',
            alt: 'Step-by-step capsule wardrobe plan',
          },
        },
      ],
    },
    quizId: 'capsule-wardrobe-quiz',
  },

  {
    id: 'versatile-pieces',
    title: 'Identifying Versatile Pieces',
    level: 4,
    category: 'wardrobe',
    estimatedMinutes: 20,
    xpReward: 150,
    prerequisites: ['capsule-wardrobe-principles'],
    order: 20,
    description: 'Discover which items earn their place through maximum outfit combinations',
    learningObjectives: [
      'Evaluate garments by versatility score across occasions',
      'Identify the characteristics that make pieces interchangeable',
      'Distinguish between versatile staples and limited-use items',
      'Build a core rotation of high-utility garments',
    ],
    content: {
      sections: [
        {
          title: 'Measuring Versatility',
          content:
            'Rate each item on three axes: occasion range (casual to formal), season range (how many months per year), and combination count (how many other items it pairs with). A dark navy chino scores high on all three: works at the office, on weekends, and in most climates. A linen beach shirt scores lower overall but high for its niche.',
          media: {
            type: 'image',
            url: '/content/images/versatility-scoring.jpg',
            alt: 'Three-axis versatility scoring method',
          },
        },
        {
          title: 'Hallmarks of Versatile Pieces',
          content:
            'Versatile pieces share common traits: neutral or muted colors, clean lines without bold patterns, moderate fit (not too slim or too loose), quality fabrics that dress up or down, and minimal branding. A well-fitted gray crewneck sweater exemplifies all five, working under a blazer or over a casual tee.',
          media: {
            type: 'image',
            url: '/content/images/versatile-hallmarks.jpg',
            alt: 'Characteristics of versatile garments',
          },
        },
        {
          title: 'The Versatility Spectrum',
          content:
            'Rank your wardrobe from most to least versatile. Top tier: white OCBD, navy chinos, gray suit, white sneakers, brown leather shoes. Mid tier: patterned shirts, colored knitwear, casual jackets. Low tier: novelty pieces, bold prints, single-occasion items. Aim for 70% top-tier items in your core rotation.',
          media: {
            type: 'image',
            url: '/content/images/versatility-spectrum.jpg',
            alt: 'Wardrobe items ranked by versatility',
          },
        },
      ],
    },
    quizId: 'versatile-pieces-quiz',
  },

  {
    id: 'cost-per-wear-thinking',
    title: 'Cost-Per-Wear Thinking',
    level: 4,
    category: 'wardrobe',
    estimatedMinutes: 18,
    xpReward: 140,
    prerequisites: ['building-foundation-wardrobe'],
    order: 21,
    description: 'Evaluate wardrobe investments by actual value, not price tags',
    learningObjectives: [
      'Calculate cost-per-wear for any garment',
      'Identify which categories deserve premium investment',
      'Recognize false economies in cheap clothing',
      'Plan purchase decisions using value-based thinking',
    ],
    content: {
      sections: [
        {
          title: 'The Cost-Per-Wear Formula',
          content:
            'Cost-per-wear = purchase price divided by number of times worn. A $300 pair of dress shoes worn 200 times costs $1.50 per wear. A $40 fast-fashion shoe worn 15 times costs $2.67 per wear. Factor in maintenance costs (resoling, dry cleaning) for a complete picture. Track your wear count to make data-driven decisions.',
          media: {
            type: 'interactive',
            url: '/content/interactive/cpw-calculator',
            alt: 'Interactive cost-per-wear calculator',
          },
        },
        {
          title: 'Where to Invest',
          content:
            'High-investment items (lowest cost-per-wear): shoes, outerwear, suits, quality denim. These get worn frequently and quality directly affects longevity. Medium investment: dress shirts, knitwear, accessories. Lower investment: t-shirts, casual basics, trend pieces. The key insight: your most-worn items deserve the most spend.',
          media: {
            type: 'image',
            url: '/content/images/investment-tiers.jpg',
            alt: 'Investment priority tiers for wardrobe categories',
          },
        },
        {
          title: 'The False Economy Trap',
          content:
            'Cheap items often cost more long-term. Fast-fashion blazers lose shape after 10 wears. Cheap shoes cannot be resoled. Low-quality fabrics pill and fade. Compare total lifetime cost: three $50 jackets over 3 years ($150) vs one $200 jacket lasting 5 years ($200 but better value per wear and appearance). Buy the best you can afford for daily-wear items.',
          media: {
            type: 'image',
            url: '/content/images/false-economy.jpg',
            alt: 'Comparison of cheap vs quality item longevity',
          },
        },
      ],
    },
    quizId: 'cost-per-wear-quiz',
  },

  {
    id: 'seasonal-rotation-strategy',
    title: 'Seasonal Rotation Strategy',
    level: 4,
    category: 'wardrobe',
    estimatedMinutes: 20,
    xpReward: 150,
    prerequisites: ['closet-organization'],
    order: 22,
    description: 'Master the art of rotating your wardrobe through seasons efficiently',
    learningObjectives: [
      'Plan wardrobe transitions between seasons',
      'Identify cross-seasonal bridge pieces',
      'Store off-season items properly to preserve quality',
      'Build a year-round capsule with seasonal extensions',
    ],
    content: {
      sections: [
        {
          title: 'The Four-Season Framework',
          content:
            'Divide your wardrobe into a year-round core (40%) and seasonal extensions (60%). Core items work in any weather: dark jeans, chinos, blazers, leather shoes. Seasonal extensions add weather-appropriate layers. Spring/summer: linen shirts, lightweight chinos, canvas sneakers. Fall/winter: wool sweaters, heavier trousers, boots, overcoats.',
          media: {
            type: 'image',
            url: '/content/images/four-season-framework.jpg',
            alt: 'Year-round core vs seasonal extensions',
          },
        },
        {
          title: 'Bridge Pieces',
          content:
            'Bridge pieces ease transitions between seasons. Lightweight merino sweaters work from early fall through spring. A cotton field jacket handles cool summer evenings and mild fall days. Layering items like vests and lightweight scarves extend the range of single-season pieces by weeks.',
          media: {
            type: 'image',
            url: '/content/images/bridge-pieces.jpg',
            alt: 'Transitional wardrobe pieces for between seasons',
          },
        },
        {
          title: 'Proper Seasonal Storage',
          content:
            'Off-season items need protection. Clean everything before storing (stains set over months). Use breathable cotton garment bags, never plastic. Add cedar blocks for moth prevention. Store shoes with cedar shoe trees. Keep stored items in a cool, dry, dark location. Vacuum-seal bulky items like down jackets to save space.',
          media: {
            type: 'image',
            url: '/content/images/seasonal-storage.jpg',
            alt: 'Proper garment storage techniques',
          },
        },
        {
          title: 'The Seasonal Audit',
          content:
            'Before each season, audit your rotation. Pull out seasonal items and check condition. Identify gaps from last year. Plan 2-3 targeted purchases to fill gaps rather than impulse shopping. Return outgoing season items cleaned and stored. This biannual ritual keeps your wardrobe efficient and prevents wasteful accumulation.',
          media: {
            type: 'image',
            url: '/content/images/seasonal-audit.jpg',
            alt: 'Biannual wardrobe audit process',
          },
        },
      ],
    },
    quizId: 'seasonal-rotation-quiz',
  },

  {
    id: 'wardrobe-editing',
    title: 'Wardrobe Editing',
    level: 4,
    category: 'wardrobe',
    estimatedMinutes: 22,
    xpReward: 160,
    prerequisites: ['capsule-wardrobe-principles'],
    order: 23,
    description: 'Learn when to keep, donate, or replace items for a curated closet',
    learningObjectives: [
      'Apply objective criteria for keep/donate/replace decisions',
      'Recognize emotional attachment that skews wardrobe judgment',
      'Execute a systematic closet purge without regret',
      'Maintain an edited wardrobe through ongoing curation',
    ],
    content: {
      sections: [
        {
          title: 'The Keep/Donate/Replace Framework',
          content:
            'Evaluate each item on three questions: Does it fit well right now? Have I worn it in the past 6 months (seasonal exceptions apply)? Does it work with at least 3 other items I own? Two or more "no" answers means it goes. One "no" means it is on probation. Keep items that pass all three without hesitation.',
          media: {
            type: 'image',
            url: '/content/images/kdr-framework.jpg',
            alt: 'Keep/Donate/Replace decision flowchart',
          },
        },
        {
          title: 'Emotional Attachment Traps',
          content:
            'Common traps: "I paid a lot for this" (sunk cost fallacy), "I might need it someday" (rarely true), "It has sentimental value" (keep 1-2 truly meaningful pieces, not 20). Aspiration items you plan to fit into "someday" create guilt, not style. Be honest about your current life and body, not a hypothetical future.',
          media: {
            type: 'image',
            url: '/content/images/attachment-traps.jpg',
            alt: 'Common emotional traps in wardrobe editing',
          },
        },
        {
          title: 'The Systematic Purge',
          content:
            'Work category by category, not item by item. Pull everything out of the closet. Sort into definite keep, definite donate, and maybe piles. Try on every "maybe" item. Photograph yourself and assess objectively. Bag up donations immediately. The goal is removing 20-30% on your first edit.',
          media: {
            type: 'video',
            url: '/content/videos/closet-purge.mp4',
            alt: 'Step-by-step closet purge demonstration',
          },
        },
        {
          title: 'Ongoing Curation',
          content:
            'Wardrobe editing is not a one-time event. Apply the "one in, one out" rule for new purchases. Do a quick monthly scan for items drifting out of rotation. Conduct a full audit every 6 months. Track what you actually wear using this app. Data-driven curation removes guesswork and ensures your wardrobe improves continuously.',
          media: {
            type: 'image',
            url: '/content/images/ongoing-curation.jpg',
            alt: 'Maintaining a curated wardrobe over time',
          },
        },
      ],
    },
    quizId: 'wardrobe-editing-quiz',
  },

  // ==========================================
  // LEVEL 5 MODULES - Style Development
  // ==========================================
  {
    id: 'finding-personal-style',
    title: 'Finding Your Personal Style',
    level: 5,
    category: 'style',
    estimatedMinutes: 25,
    xpReward: 200,
    prerequisites: [],
    order: 24,
    description: 'Discover and define the style that authentically represents you',
    learningObjectives: [
      'Identify your lifestyle and style needs through self-assessment',
      'Analyze your existing wardrobe for recurring style themes',
      'Articulate your personal style in concrete terms',
      'Create a style direction that aligns with your identity',
    ],
    content: {
      sections: [
        {
          title: 'The Style Self-Assessment',
          content:
            'Personal style starts with self-knowledge. Assess three dimensions: your daily activities (what you actually do 80% of the time), your comfort zone (fabrics, fits, and colors you gravitate toward), and your aspirations (how you want others to perceive you). Most men dress for who they wish they were, not who they are. Authentic style bridges both.',
          media: {
            type: 'interactive',
            url: '/content/interactive/style-assessment',
            alt: 'Interactive personal style assessment',
          },
        },
        {
          title: 'Reading Your Wardrobe Patterns',
          content:
            'Your current wardrobe reveals unconscious preferences. Sort your most-worn items and look for patterns: Do you favor structured or relaxed silhouettes? Earth tones or cool colors? Minimal or detailed pieces? These patterns are your starting point. Resist the urge to reinvent yourself entirely. Amplify what already works.',
          media: {
            type: 'image',
            url: '/content/images/wardrobe-patterns.jpg',
            alt: 'Identifying style patterns in existing wardrobe',
          },
        },
        {
          title: 'Defining Your Style in Words',
          content:
            'Distill your style into 3-5 descriptive words. Examples: "clean, modern, understated" or "rugged, textured, earthy" or "sharp, bold, tailored." These words become your filter for every purchase decision. If a piece does not match at least 2 of your style words, it does not belong in your wardrobe.',
          media: {
            type: 'image',
            url: '/content/images/style-words.jpg',
            alt: 'Style descriptor word examples',
          },
        },
        {
          title: 'Creating Your Style Direction',
          content:
            'Compile a style reference board (physical or digital). Include outfit photos you admire, textures and colors that resonate, and lifestyle images that capture your aesthetic. Look for the common thread. Your style direction should feel like a more intentional version of yourself, not a costume. Refine it over 3-6 months as you learn what truly works.',
          media: {
            type: 'image',
            url: '/content/images/style-direction.jpg',
            alt: 'Building a personal style direction board',
          },
        },
      ],
    },
    quizId: 'personal-style-quiz',
  },

  {
    id: 'style-icons-inspiration',
    title: 'Style Icons & Inspiration',
    level: 5,
    category: 'style',
    estimatedMinutes: 20,
    xpReward: 160,
    prerequisites: [],
    order: 25,
    description: "Study influential men's style and learn to draw inspiration without copying",
    learningObjectives: [
      'Analyze what makes certain style icons timeless',
      'Extract principles from admired looks rather than copying specifics',
      'Adapt inspiration from different eras and contexts to modern life',
      'Build a curated reference library for ongoing style development',
    ],
    content: {
      sections: [
        {
          title: 'What Makes a Style Icon',
          content:
            "Style icons share key traits: consistency (they have a recognizable look), appropriateness (they dress for their life and body), attention to detail (fit, accessories, grooming align), and confidence (they own their choices). Study Steve McQueen's effortless casual, David Beckham's range, or Idris Elba's tailored confidence. Notice how each adapted fashion to their personality, not the reverse.",
          media: {
            type: 'image',
            url: '/content/images/style-icons.jpg',
            alt: "Timeless men's style icon examples",
          },
        },
        {
          title: 'Extracting Principles, Not Outfits',
          content:
            'Never copy an outfit piece by piece. Instead, extract the principle. If you admire a look, ask: What makes this work? It might be the color harmony, the proportion balance, the texture contrast, or the formality mix. These principles transfer to your wardrobe regardless of budget or body type. A $3000 outfit and a $300 outfit can share the same underlying principles.',
          media: {
            type: 'image',
            url: '/content/images/extracting-principles.jpg',
            alt: 'Breaking down style principles from inspiration',
          },
        },
        {
          title: 'Adapting Across Eras',
          content:
            'Classic eras offer rich inspiration when adapted thoughtfully. 1960s Ivy League: slim chinos, OCBDs, loafers (translates directly). 1950s Italian: high-waisted trousers, fitted knits (adapt the silhouette principle, not the exact proportions). Avoid costume territory by keeping silhouettes modern and mixing era-inspired pieces with contemporary items.',
          media: {
            type: 'image',
            url: '/content/images/era-adaptation.jpg',
            alt: 'Adapting vintage style principles to modern wardrobes',
          },
        },
        {
          title: 'Building Your Reference Library',
          content:
            'Curate a living inspiration folder. Save outfit photos, editorial looks, and street style that resonates. Organize by occasion or mood. Revisit monthly and prune what no longer fits your direction. Over time, patterns emerge that sharpen your style instinct. Use this library when shopping to stay focused and avoid impulse purchases.',
          media: {
            type: 'image',
            url: '/content/images/reference-library.jpg',
            alt: 'Organizing a style reference collection',
          },
        },
      ],
    },
    quizId: 'style-icons-quiz',
  },

  {
    id: 'adapting-trends',
    title: 'Adapting Trends Appropriately',
    level: 5,
    category: 'style',
    estimatedMinutes: 18,
    xpReward: 150,
    prerequisites: ['finding-personal-style'],
    order: 26,
    description: 'Filter fashion trends through your personal style for authentic updates',
    learningObjectives: [
      'Distinguish between fleeting fads and lasting trends',
      'Evaluate whether a trend aligns with your personal style',
      'Incorporate trends at the right scale and investment level',
      'Avoid trend-chasing that undermines wardrobe coherence',
    ],
    content: {
      sections: [
        {
          title: 'Fads vs Trends vs Classics',
          content:
            'Fads last 1-2 seasons (novelty prints, extreme silhouettes). Trends last 3-7 years (wide-leg trousers, earth tones, relaxed tailoring). Classics endure decades (navy blazers, white shirts, straight-leg jeans). Invest proportionally: ignore most fads, selectively adopt trends, and build your foundation on classics. Roughly 70% classics, 20% trends, 10% personal flair.',
          media: {
            type: 'image',
            url: '/content/images/fad-trend-classic.jpg',
            alt: 'Spectrum from fads to classics with examples',
          },
        },
        {
          title: 'The Personal Filter',
          content:
            'Before adopting a trend, run it through your style words. Does relaxed tailoring fit your "sharp, clean" aesthetic? Probably not. Does it fit "modern, effortless"? Absolutely. If a trend contradicts your core style direction, skip it regardless of popularity. The goal is evolution, not revolution. Your wardrobe should update gradually, not reinvent itself each season.',
          media: {
            type: 'image',
            url: '/content/images/trend-filter.jpg',
            alt: 'Filtering trends through personal style',
          },
        },
        {
          title: 'Incorporating Trends Wisely',
          content:
            'Adopt trends at the lowest-cost entry point first. Try a trendy color through an accessory before committing to a suit. Test a silhouette shift through casual pieces before tailored ones. Limit trend pieces to 2-3 per season. Mix them with proven staples so they enhance rather than overwhelm your existing wardrobe.',
          media: {
            type: 'image',
            url: '/content/images/trend-adoption.jpg',
            alt: 'Low-risk trend adoption strategies',
          },
        },
      ],
    },
    quizId: 'adapting-trends-quiz',
  },

  {
    id: 'building-signature-looks',
    title: 'Building Signature Looks',
    level: 5,
    category: 'style',
    estimatedMinutes: 22,
    xpReward: 170,
    prerequisites: ['finding-personal-style'],
    order: 27,
    description: 'Create recognizable, repeatable outfits that define your style identity',
    learningObjectives: [
      'Understand what makes an outfit a signature look',
      'Build repeatable outfit formulas for different occasions',
      'Use consistent elements to create visual recognition',
      'Evolve signature looks over time without losing identity',
    ],
    content: {
      sections: [
        {
          title: 'What Makes a Signature Look',
          content:
            'A signature look is not wearing the same outfit daily. It is a consistent visual language: recurring color palettes, preferred silhouettes, distinctive accessories, or characteristic combinations. Think of it as your "uniform formula." When people can describe your style in a sentence, you have a signature. Example: "He always looks sharp in fitted navy with brown leather details."',
          media: {
            type: 'image',
            url: '/content/images/signature-look-concept.jpg',
            alt: 'Signature look examples across different styles',
          },
        },
        {
          title: 'Building Outfit Formulas',
          content:
            'Create 3-4 reliable formulas for your main occasions. Work formula: navy trousers + OCBD + leather shoes + minimal watch. Weekend formula: dark denim + quality tee + casual jacket + white sneakers. Evening formula: dark suit + textured shirt + statement accessory. Each formula allows variation within a consistent framework.',
          media: {
            type: 'image',
            url: '/content/images/outfit-formulas.jpg',
            alt: 'Repeatable outfit formula templates',
          },
        },
        {
          title: 'Signature Elements',
          content:
            'Pick 1-2 consistent elements that thread through all your outfits. This could be: always wearing a particular watch, consistently choosing suede over smooth leather, gravitating toward Breton stripes, or preferring unstructured over padded blazers. These recurring details create visual continuity without monotony.',
          media: {
            type: 'image',
            url: '/content/images/signature-elements.jpg',
            alt: 'Consistent style elements across outfits',
          },
        },
        {
          title: 'Evolving Your Signature',
          content:
            'Signature style should evolve, not stagnate. Each season, reassess your formulas. Swap one element for something that reflects current preferences or subtle trend influence. Keep the core structure but refresh the details. This maintains recognition while preventing your style from feeling dated. The best-dressed men look consistent across years but never frozen in time.',
          media: {
            type: 'image',
            url: '/content/images/evolving-signature.jpg',
            alt: 'Style evolution while maintaining signature identity',
          },
        },
      ],
    },
    quizId: 'signature-looks-quiz',
  },

  // ==========================================
  // LEVEL 6 MODULES - Advanced Coordination
  // ==========================================
  {
    id: 'pattern-mixing',
    title: 'Pattern Mixing Mastery',
    level: 6,
    category: 'style',
    estimatedMinutes: 25,
    xpReward: 200,
    prerequisites: ['color-combinations'],
    order: 28,
    description: 'Combine multiple patterns confidently using scale, color, and type rules',
    learningObjectives: [
      'Apply the rule of varying pattern scales for harmonious mixing',
      'Combine different pattern types (stripes, checks, dots) effectively',
      'Use a shared color thread to unify patterned garments',
      'Recognize when pattern mixing crosses from bold to chaotic',
    ],
    content: {
      sections: [
        {
          title: 'The Scale Rule',
          content:
            'The fundamental principle of pattern mixing is varying scale. Pair a large-scale pattern with a small-scale pattern: wide-stripe shirt with micro-dot tie, bold windowpane suit with fine stripe shirt. Same-scale patterns compete and create visual vibration. Different scales create hierarchy, letting each piece breathe while contributing to the whole.',
          media: {
            type: 'image',
            url: '/content/images/pattern-scale.jpg',
            alt: 'Large and small scale pattern combinations',
          },
        },
        {
          title: 'Pattern Type Combinations',
          content:
            'Some pattern types pair better than others. Stripes + dots is classic and foolproof. Stripes + checks works when scales differ. Plaids + florals (in pocket squares) adds personality. Avoid doubling pattern types at the same scale: two similar-width stripes or two same-size checks clash. When in doubt, keep one piece solid as an anchor.',
          media: {
            type: 'image',
            url: '/content/images/pattern-types.jpg',
            alt: 'Compatible and clashing pattern type pairings',
          },
        },
        {
          title: 'The Color Thread',
          content:
            'Successful pattern mixing always shares at least one color between patterned pieces. A blue windowpane suit pairs beautifully with a burgundy-and-blue striped tie because the blue connects them. Without a shared color, even correctly scaled patterns feel disjointed. Identify the linking color before combining.',
          media: {
            type: 'image',
            url: '/content/images/color-thread.jpg',
            alt: 'Shared color connecting multiple patterns',
          },
        },
        {
          title: 'Knowing Your Limits',
          content:
            'Maximum three patterns in one outfit for most settings. Start with two patterned pieces and one solid. Graduate to three patterns only after mastering two. In professional settings, two patterns maximum. Save three-pattern combinations for social and creative contexts. If you feel uncertain about a combination, simplify. Confidence is part of pulling it off.',
          media: {
            type: 'image',
            url: '/content/images/pattern-limits.jpg',
            alt: 'Pattern mixing complexity levels',
          },
        },
      ],
    },
    quizId: 'pattern-mixing-quiz',
  },

  {
    id: 'texture-play',
    title: 'Texture Play',
    level: 6,
    category: 'style',
    estimatedMinutes: 20,
    xpReward: 170,
    prerequisites: ['monochromatic-styling'],
    order: 29,
    description: 'Add depth and visual interest to outfits through deliberate texture combinations',
    learningObjectives: [
      'Identify the major texture categories in menswear fabrics',
      'Create contrast and depth through strategic texture pairing',
      'Match texture formality to occasion appropriateness',
      'Use texture as the primary interest driver in neutral outfits',
    ],
    content: {
      sections: [
        {
          title: 'The Texture Spectrum',
          content:
            'Menswear textures range from smooth to rough, matte to shiny. Smooth and shiny (silk, polished leather, worsted wool) reads formal. Rough and matte (tweed, suede, flannel, linen) reads casual. In between: cotton oxford, knit ties, pebbled leather. Understanding where each fabric falls on this spectrum lets you control formality through texture alone.',
          media: {
            type: 'image',
            url: '/content/images/texture-spectrum.jpg',
            alt: 'Fabric texture spectrum from smooth to rough',
          },
        },
        {
          title: 'Creating Texture Contrast',
          content:
            'The most visually interesting outfits mix 2-3 textures. Pair smooth with rough: a suede jacket over a crisp cotton shirt. Combine matte with subtle sheen: flannel trousers with a silk-blend knit. Mix structured with relaxed: a sharp wool blazer over a slubby linen tee. Texture contrast is especially powerful in monochromatic outfits where color contrast is absent.',
          media: {
            type: 'image',
            url: '/content/images/texture-contrast.jpg',
            alt: 'Outfits demonstrating texture contrast',
          },
        },
        {
          title: 'Texture and Formality',
          content:
            'Use texture to calibrate formality precisely. A navy suit in smooth worsted wool is boardroom formal. The same navy suit in flannel is smart-casual Friday. A blazer in hopsack linen is weekend appropriate. Suede shoes dress down any outfit one notch. A silk pocket square dresses it up one notch. Master this and you control the message of every outfit.',
          media: {
            type: 'image',
            url: '/content/images/texture-formality.jpg',
            alt: 'Same color garments at different formality via texture',
          },
        },
      ],
    },
    quizId: 'texture-play-quiz',
  },

  {
    id: 'proportions-silhouette',
    title: 'Proportions & Silhouette',
    level: 6,
    category: 'style',
    estimatedMinutes: 22,
    xpReward: 180,
    prerequisites: [],
    order: 30,
    description:
      'Understand how garment proportions create flattering silhouettes for your body type',
    learningObjectives: [
      'Identify the major body type categories and their style considerations',
      'Apply proportion principles to create balanced visual silhouettes',
      'Use fit and structure to enhance your natural frame',
      'Avoid common proportion mistakes that throw off visual balance',
    ],
    content: {
      sections: [
        {
          title: 'Body Type Awareness',
          content:
            'The five common body types each have different style strengths. Athletic/V-shape: broader shoulders, narrower waist. Rectangle: even proportions throughout. Triangle: wider at hips. Inverted triangle: very broad shoulders. Round: fullness at the midsection. Knowing your type is not about hiding flaws, it is about choosing cuts that work with your proportions.',
          media: {
            type: 'image',
            url: '/content/images/body-types.jpg',
            alt: 'Five common male body types illustrated',
          },
        },
        {
          title: 'The Rule of Thirds',
          content:
            'Visually divide outfits into thirds, not halves. A jacket ending at mid-hip creates a 1/3 top, 2/3 bottom split that is more dynamic than a 50/50 divide. Tucking in a shirt with a belt creates a visible waistline that anchors proportions. High-rise trousers lengthen legs. Avoid cutting your body at the midpoint, which shortens the visual line.',
          media: {
            type: 'image',
            url: '/content/images/rule-of-thirds.jpg',
            alt: 'Outfit proportions using the rule of thirds',
          },
        },
        {
          title: 'Structure and Fit',
          content:
            'Structured garments (padded shoulders, lined blazers, firm fabrics) add definition to slim frames. Soft, unstructured pieces (unconstructed blazers, drapy knits) work better on already-athletic builds. Slim-fit flatters lean bodies. Regular or relaxed fits suit broader frames. The goal is enhancing your silhouette, not fighting it.',
          media: {
            type: 'image',
            url: '/content/images/structure-fit.jpg',
            alt: 'Structured vs unstructured garments on different builds',
          },
        },
        {
          title: 'Common Proportion Mistakes',
          content:
            'Oversized tops on short frames overwhelm. Excessively slim fits on larger builds restrict and highlight rather than flatter. Low-rise trousers shorten the leg line. Jackets too long for your torso create a boxy look. The fix: always prioritize shoulder fit in jackets, ensure trousers break at the right point, and keep proportions relative to your actual dimensions.',
          media: {
            type: 'image',
            url: '/content/images/proportion-mistakes.jpg',
            alt: 'Before and after proportion corrections',
          },
        },
      ],
    },
    quizId: 'proportions-quiz',
  },

  {
    id: 'advanced-color-coordination',
    title: 'Advanced Color Coordination',
    level: 6,
    category: 'color',
    estimatedMinutes: 25,
    xpReward: 200,
    prerequisites: ['color-combinations', 'personal-color-analysis'],
    order: 31,
    description: 'Master sophisticated multi-color outfits using advanced harmony techniques',
    learningObjectives: [
      'Apply split-complementary and tetradic color schemes to outfits',
      'Balance saturation and value across multi-color combinations',
      'Coordinate colors across all outfit elements including accessories',
      'Develop an intuitive sense for advanced color harmony',
    ],
    content: {
      sections: [
        {
          title: 'Beyond Basic Harmony',
          content:
            'Split-complementary uses one base color and the two colors adjacent to its complement (e.g., navy base with rust and golden yellow accents). Tetradic schemes use four colors forming a rectangle on the wheel. These schemes create richer, more dynamic outfits than simple complementary or analogous combinations. The key is maintaining one dominant color at 60% or more.',
          media: {
            type: 'image',
            url: '/content/images/advanced-harmony.jpg',
            alt: 'Split-complementary and tetradic color schemes',
          },
        },
        {
          title: 'Saturation and Value Balance',
          content:
            'When using multiple colors, vary their saturation and value to prevent visual competition. Keep one color at full saturation and mute the others. Pair a vivid burgundy tie with muted olive trousers and a soft gray jacket. If all colors are equally vivid, the outfit feels costumey. If all are equally muted, it feels flat. Contrast in saturation creates visual depth.',
          media: {
            type: 'image',
            url: '/content/images/saturation-balance.jpg',
            alt: 'Saturation hierarchy in multi-color outfits',
          },
        },
        {
          title: 'Full-Outfit Color Coordination',
          content:
            'Advanced coordination means every element participates: suit/jacket, shirt, tie or scarf, pocket square, belt, shoes, watch strap, socks. Create a color story where each piece connects to at least one other. Shoes and belt share a leather tone. Tie picks up a secondary color from the shirt. Pocket square echoes the jacket tone. Nothing feels random.',
          media: {
            type: 'interactive',
            url: '/content/interactive/outfit-color-builder',
            alt: 'Interactive full-outfit color coordination tool',
          },
        },
        {
          title: 'Developing Color Intuition',
          content:
            'Color mastery ultimately becomes intuitive. Train your eye by analyzing outfits in the wild: what works and why? Practice with low-stakes combinations at home before wearing them out. Keep a log of combinations that drew compliments. Over time, you will develop an instinct for balance that transcends rules. The rules are training wheels; intuition is the destination.',
          media: {
            type: 'image',
            url: '/content/images/color-intuition.jpg',
            alt: 'Developing color sense through practice',
          },
        },
      ],
    },
    quizId: 'advanced-color-quiz',
  },
]

// ==========================================
// QUIZ DEFINITIONS
// ==========================================

export const QUIZZES: Quiz[] = [
  {
    id: 'wardrobe-basics-quiz',
    moduleId: 'wardrobe-basics-101',
    title: 'Wardrobe Basics Assessment',
    passingScore: 70,
    timeLimit: 300,
    questions: [
      {
        id: 'wb-q1',
        question: 'How many core wardrobe categories should a well-organized closet have?',
        type: 'multiple-choice',
        options: ['3', '5', '7', '10'],
        correctAnswer: '5',
        explanation:
          'The five core categories are: Tops, Bottoms, Outerwear, Footwear, and Accessories.',
        points: 10,
      },
      {
        id: 'wb-q2',
        question: 'Which of the following is NOT a foundation piece every gentleman should own?',
        type: 'multiple-choice',
        options: ['White dress shirt', 'Navy blazer', 'Leather jacket', 'Dark denim jeans'],
        correctAnswer: 'Leather jacket',
        explanation:
          'While stylish, leather jackets are not considered essential foundation pieces. They are trend items that complement a solid foundation.',
        points: 10,
      },
      {
        id: 'wb-q3',
        question: 'What is the ideal stitch density for quality garment construction?',
        type: 'multiple-choice',
        options: [
          '4-6 stitches per inch',
          '8-10 stitches per inch',
          '12-14 stitches per inch',
          '16-18 stitches per inch',
        ],
        correctAnswer: '8-10 stitches per inch',
        explanation:
          'Quality garments typically have 8-10 stitches per inch, providing durability without excessive stiffness.',
        points: 10,
      },
      {
        id: 'wb-q4',
        question: 'Select all proper garment care practices:',
        type: 'multiple-choice',
        options: [
          'Using plastic hangers for suits',
          'Rotating shoes to allow them to air out',
          'Brushing suits after each wear',
          'Washing dress shirts in hot water',
        ],
        correctAnswer: 'Rotating shoes to allow them to air out,Brushing suits after each wear',
        explanation:
          'Rotating shoes and brushing suits are proper care practices. Use wooden hangers for suits, not plastic, and wash dress shirts in warm or cool water.',
        points: 20,
      },
      {
        id: 'wb-q5',
        question: 'Leather dress shoes are an essential foundation piece.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'True',
        explanation:
          'Quality leather dress shoes (typically in brown and black) are cornerstone foundation pieces.',
        points: 10,
      },
    ],
  },

  {
    id: 'color-wheel-quiz',
    moduleId: 'color-wheel-introduction',
    title: 'Color Theory Fundamentals',
    passingScore: 75,
    timeLimit: 360,
    questions: [
      {
        id: 'cw-q1',
        question: 'Which of the following are primary colors?',
        type: 'multiple-choice',
        options: [
          'Red, Blue, Yellow',
          'Red, Green, Blue',
          'Red, Yellow, Orange',
          'Blue, Green, Purple',
        ],
        correctAnswer: 'Red, Blue, Yellow',
        explanation:
          'In traditional color theory, red, blue, and yellow are the three primary colors.',
        points: 10,
      },
      {
        id: 'cw-q2',
        question: 'What is the complementary color of blue on the color wheel?',
        type: 'multiple-choice',
        options: ['Red', 'Orange', 'Green', 'Purple'],
        correctAnswer: 'Orange',
        explanation:
          'Complementary colors sit directly opposite each other on the color wheel. Blue and orange are complements.',
        points: 10,
      },
      {
        id: 'cw-q3',
        question: 'Select all warm colors:',
        type: 'multiple-choice',
        options: ['Red', 'Orange', 'Blue', 'Yellow', 'Green', 'Purple'],
        correctAnswer: 'Red,Orange,Yellow',
        explanation:
          'Warm colors include red, orange, yellow, and their variations. Cool colors are blue, green, and purple.',
        points: 15,
      },
      {
        id: 'cw-q4',
        question: 'What does the 60-30-10 rule refer to in color coordination?',
        type: 'multiple-choice',
        options: [
          'Color brightness percentages',
          'Dominant, secondary, and accent color proportions',
          'Warm to cool color ratios',
          'Primary to secondary to tertiary color distribution',
        ],
        correctAnswer: 'Dominant, secondary, and accent color proportions',
        explanation:
          'The 60-30-10 rule suggests using 60% dominant color, 30% secondary color, and 10% accent color in an outfit.',
        points: 15,
      },
      {
        id: 'cw-q5',
        question: 'Analogous colors sit opposite each other on the color wheel.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'False',
        explanation:
          'Analogous colors sit ADJACENT to each other on the color wheel. Complementary colors sit opposite.',
        points: 10,
      },
    ],
  },

  {
    id: 'seasonal-color-quiz',
    moduleId: 'seasonal-color-analysis',
    title: 'Seasonal Color Analysis',
    passingScore: 75,
    timeLimit: 300,
    questions: [
      {
        id: 'sc-q1',
        question: 'If your wrist veins appear greenish, what is your likely undertone?',
        type: 'multiple-choice',
        options: ['Warm', 'Cool', 'Neutral', 'Cannot determine from veins'],
        correctAnswer: 'Warm',
        explanation:
          'Greenish veins typically indicate warm undertones, while bluish veins suggest cool undertones.',
        points: 10,
      },
      {
        id: 'sc-q2',
        question: 'Which color season is characterized as cool and muted?',
        type: 'multiple-choice',
        options: ['Spring', 'Summer', 'Autumn', 'Winter'],
        correctAnswer: 'Summer',
        explanation:
          'Summer is cool and muted. Spring is warm and bright. Autumn is warm and muted. Winter is cool and bright.',
        points: 10,
      },
      {
        id: 'sc-q3',
        question: 'Select colors appropriate for the Autumn season:',
        type: 'multiple-choice',
        options: ['Terracotta', 'Powder blue', 'Olive green', 'Lavender', 'Rust', 'Royal blue'],
        correctAnswer: 'Terracotta,Olive green,Rust',
        explanation:
          'Autumn colors are warm and muted: terracotta, olive, rust, camel, forest green. Powder blue, lavender, and royal blue belong to other seasons.',
        points: 15,
      },
      {
        id: 'sc-q4',
        question: 'How many colors should you include in your personal palette?',
        type: 'multiple-choice',
        options: ['4-6', '8-12', '15-20', '25+'],
        correctAnswer: '8-12',
        explanation:
          'A personal palette of 8-12 colors provides variety while maintaining cohesion and simplifying shopping decisions.',
        points: 10,
      },
      {
        id: 'sc-q5',
        question: 'Gold jewelry typically flatters warm undertones better than silver.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'True',
        explanation:
          'Gold complements warm undertones, while silver flatters cool undertones. Neutral undertones can wear both.',
        points: 10,
      },
    ],
  },

  {
    id: 'monochromatic-quiz',
    moduleId: 'monochromatic-styling',
    title: 'Monochromatic Styling',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'ms-q1',
        question: 'What is the difference between monochromatic and tonal dressing?',
        type: 'multiple-choice',
        options: [
          'Monochromatic uses one color family; tonal uses adjacent colors',
          'There is no difference',
          'Monochromatic is formal; tonal is casual',
          'Monochromatic uses patterns; tonal does not',
        ],
        correctAnswer: 'Monochromatic uses one color family; tonal uses adjacent colors',
        explanation:
          'Monochromatic stays within one color family (e.g., all blues). Tonal uses adjacent colors (e.g., blue, teal, aqua).',
        points: 15,
      },
      {
        id: 'ms-q2',
        question: 'What is the primary way to add visual interest to a monochrome outfit?',
        type: 'multiple-choice',
        options: [
          'Texture mixing',
          'Adding bright colors',
          'Wearing all matte fabrics',
          'Using identical shades',
        ],
        correctAnswer: 'Texture mixing',
        explanation:
          'Varying textures (matte, shiny, smooth, textured) prevents monochrome outfits from appearing flat or boring.',
        points: 10,
      },
      {
        id: 'ms-q3',
        question: 'Your shirt and tie should be the exact same color in a monochrome outfit.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'False',
        explanation:
          'Avoid exact color matching. Use different shades within the same color family for depth and visual interest.',
        points: 10,
      },
      {
        id: 'ms-q4',
        question: 'Select appropriate texture combinations for monochrome styling:',
        type: 'multiple-choice',
        options: [
          'Wool trousers + cotton shirt',
          'All cotton garments',
          'Silk tie + wool suit',
          'All polyester pieces',
        ],
        correctAnswer: 'Wool trousers + cotton shirt,Silk tie + wool suit',
        explanation:
          'Mixing different textures (wool, cotton, silk) creates visual interest. Avoid using all the same fabric.',
        points: 15,
      },
    ],
  },

  {
    id: 'tie-knots-quiz',
    moduleId: 'tie-knots-mastery',
    title: 'Tie Knots & Neckwear',
    passingScore: 75,
    timeLimit: 420,
    questions: [
      {
        id: 'tk-q1',
        question: 'Which tie knot is asymmetric and best for everyday wear?',
        type: 'multiple-choice',
        options: ['Four-in-Hand', 'Half-Windsor', 'Full Windsor', 'Pratt'],
        correctAnswer: 'Four-in-Hand',
        explanation:
          'The Four-in-Hand is asymmetric, slim, and versatile for everyday business and casual occasions.',
        points: 10,
      },
      {
        id: 'tk-q2',
        question: 'What collar type requires a larger tie knot like the Full Windsor?',
        type: 'multiple-choice',
        options: ['Point collar', 'Button-down collar', 'Spread or cutaway collar', 'Wing collar'],
        correctAnswer: 'Spread or cutaway collar',
        explanation:
          'Spread and cutaway collars have wider collar spreads that require larger, more triangular knots like the Full Windsor.',
        points: 10,
      },
      {
        id: 'tk-q3',
        question: 'Where should the tip of your tie end?',
        type: 'multiple-choice',
        options: ['Above the belt', 'At the belt buckle', 'Below the belt', 'Mid-thigh'],
        correctAnswer: 'At the belt buckle',
        explanation: 'The tie tip should just reach your belt buckle for proper proportions.',
        points: 10,
      },
      {
        id: 'tk-q4',
        question: 'Standard tie width for average builds is:',
        type: 'multiple-choice',
        options: ['2-2.5 inches', '3-3.25 inches', '3.5-4 inches', '4.5-5 inches'],
        correctAnswer: '3-3.25 inches',
        explanation:
          'Ties for average builds should be 3-3.25 inches wide, matching most lapel widths. Slim builds: 2.5-3 inches. Larger builds: 3.25-3.75 inches.',
        points: 10,
      },
      {
        id: 'tk-q5',
        question: 'Select appropriate pattern mixing rules for ties:',
        type: 'multiple-choice',
        options: [
          'Mix pattern scales (large with small)',
          'Use all the same pattern size',
          'Match one color between shirt and tie',
          'Never wear patterns together',
        ],
        correctAnswer:
          'Mix pattern scales (large with small),Match one color between shirt and tie',
        explanation:
          'Mix different pattern scales and ensure at least one shared color between garments. Avoid same-size patterns competing.',
        points: 15,
      },
    ],
  },

  {
    id: 'metal-matching-quiz',
    moduleId: 'metal-matching-guide',
    title: 'Metal Matching & Accessories',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'mm-q1',
        question: 'What is the traditional rule for metal accessories?',
        type: 'multiple-choice',
        options: [
          'Always mix metals',
          'Match all visible metals',
          'Only wear one metal piece',
          "Metals don't need to coordinate",
        ],
        correctAnswer: 'Match all visible metals',
        explanation:
          'The classic rule is to match all visible metals (watch, belt buckle, cufflinks, rings) for a cohesive look.',
        points: 10,
      },
      {
        id: 'mm-q2',
        question: 'Which metals best complement warm undertones?',
        type: 'multiple-choice',
        options: [
          'Silver and platinum',
          'Gold and rose gold',
          'Chrome and white gold',
          'All metals equally',
        ],
        correctAnswer: 'Gold and rose gold',
        explanation:
          'Warm undertones are complemented by warm metals: gold, rose gold, bronze, and copper.',
        points: 10,
      },
      {
        id: 'mm-q3',
        question:
          'When mixing metals in a modern style, what proportion should the dominant metal be?',
        type: 'multiple-choice',
        options: ['40%', '50%', '60%', '80%'],
        correctAnswer: '60%',
        explanation:
          'In modern mixed-metal styling, aim for 60% dominant metal and 40% accent metal.',
        points: 10,
      },
      {
        id: 'mm-q4',
        question: 'Neutral undertones can wear any metal color.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'True',
        explanation:
          'Neutral undertones have the flexibility to wear silver, gold, or mixed metals without clashing.',
        points: 10,
      },
      {
        id: 'mm-q5',
        question: 'Two-tone watches are useful for:',
        type: 'multiple-choice',
        options: [
          'Formal events only',
          'Bridging silver and gold accessories',
          'Casual wear only',
          'They should be avoided',
        ],
        correctAnswer: 'Bridging silver and gold accessories',
        explanation:
          'Two-tone watches (combining silver and gold) allow you to coordinate with both metal families in one piece.',
        points: 10,
      },
    ],
  },

  // Additional quizzes for new modules
  {
    id: 'foundation-wardrobe-quiz',
    moduleId: 'building-foundation-wardrobe',
    title: 'Foundation Wardrobe Assessment',
    passingScore: 70,
    timeLimit: 300,
    questions: [
      {
        id: 'fw-q1',
        question: 'How many essential foundation items should form the core of your wardrobe?',
        type: 'multiple-choice',
        options: ['5', '10', '15', '25'],
        correctAnswer: '15',
        explanation:
          'The essential 15 pieces provide the foundation for countless outfit combinations.',
        points: 10,
      },
      {
        id: 'fw-q2',
        question: 'What does "cost per wear" help you evaluate?',
        type: 'multiple-choice',
        options: [
          'Resale value',
          'True value of investment pieces',
          'Dry cleaning costs',
          'Brand reputation',
        ],
        correctAnswer: 'True value of investment pieces',
        explanation:
          'Cost per wear divides purchase price by number of wears, showing the real value of quality pieces.',
        points: 10,
      },
      {
        id: 'fw-q3',
        question: 'Which items should you invest more money in?',
        type: 'multiple-choice',
        options: ['T-shirts', 'Trendy items', 'Shoes and outerwear', 'Seasonal pieces'],
        correctAnswer: 'Shoes and outerwear',
        explanation:
          'Shoes are most visible and long-lasting. Outerwear is worn daily in season. Both warrant higher investment.',
        points: 15,
      },
      {
        id: 'fw-q4',
        question: 'A versatile piece should pair with at least how many other items?',
        type: 'multiple-choice',
        options: ['2', '3', '5', '10'],
        correctAnswer: '5',
        explanation:
          'Each foundation piece should work with at least 5 other items for maximum versatility.',
        points: 10,
      },
      {
        id: 'fw-q5',
        question: 'Which is NOT a recommended foundation piece?',
        type: 'multiple-choice',
        options: ['White dress shirt', 'Navy blazer', 'Graphic t-shirt', 'Dark denim jeans'],
        correctAnswer: 'Graphic t-shirt',
        explanation:
          'Graphic t-shirts are personal expression pieces, not versatile foundations. Solid colors are more adaptable.',
        points: 10,
      },
    ],
  },

  {
    id: 'closet-organization-quiz',
    moduleId: 'closet-organization',
    title: 'Closet Organization',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'co-q1',
        question: 'What is the best way to organize clothes within a category?',
        type: 'multiple-choice',
        options: ['By brand', 'By color from light to dark', 'By purchase date', 'Randomly'],
        correctAnswer: 'By color from light to dark',
        explanation: 'Organizing by color makes outfit selection intuitive and visually pleasing.',
        points: 10,
      },
      {
        id: 'co-q2',
        question: 'Which hanger type is best for suits and blazers?',
        type: 'multiple-choice',
        options: ['Wire hangers', 'Plastic hangers', 'Wooden hangers', 'Velvet hangers'],
        correctAnswer: 'Wooden hangers',
        explanation:
          'Wooden hangers maintain the shape of suits and blazers, preventing shoulder bumps.',
        points: 10,
      },
      {
        id: 'co-q3',
        question: 'Sweaters should be hung on hangers.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'False',
        explanation:
          'Never hang sweaters. They stretch out. Fold them and place on shelves instead.',
        points: 10,
      },
      {
        id: 'co-q4',
        question: 'The one-year rule suggests donating items not worn in how long?',
        type: 'multiple-choice',
        options: ['3 months', '6 months', '12 months', '24 months'],
        correctAnswer: '12 months',
        explanation: "If you haven't worn something in 12 months, you likely don't need it.",
        points: 10,
      },
    ],
  },

  {
    id: 'color-season-quiz',
    moduleId: 'seasonal-color-analysis',
    title: 'Discover Your Color Season',
    passingScore: 70,
    timeLimit: 300,
    questions: [
      {
        id: 'cs-q1',
        question: 'What determines your color season?',
        type: 'multiple-choice',
        options: [
          'Your favorite colors',
          'Your natural coloring (skin, hair, eyes)',
          'Current fashion trends',
          'Your wardrobe colors',
        ],
        correctAnswer: 'Your natural coloring (skin, hair, eyes)',
        explanation:
          'Your color season is based on your natural features, not preferences or trends.',
        points: 10,
      },
      {
        id: 'cs-q2',
        question: 'Spring and Autumn are characterized by what undertone?',
        type: 'multiple-choice',
        options: ['Cool', 'Warm', 'Neutral', 'Mixed'],
        correctAnswer: 'Warm',
        explanation: 'Spring and Autumn both have warm undertones. Summer and Winter are cool.',
        points: 10,
      },
      {
        id: 'cs-q3',
        question: 'Which best describes the Winter color season?',
        type: 'multiple-choice',
        options: ['Warm and muted', 'Cool and muted', 'Warm and bright', 'Cool and bright'],
        correctAnswer: 'Cool and bright',
        explanation: 'Winter is cool and bright/vivid. Summer is cool but muted.',
        points: 10,
      },
      {
        id: 'cs-q4',
        question: 'If gold jewelry flatters you more than silver, you likely have:',
        type: 'multiple-choice',
        options: ['Warm undertones', 'Cool undertones', 'Neutral undertones', "It doesn't matter"],
        correctAnswer: 'Warm undertones',
        explanation:
          'Gold complements warm undertones (Spring, Autumn). Silver flatters cool undertones.',
        points: 10,
      },
      {
        id: 'cs-q5',
        question: 'Bluish wrist veins typically indicate cool undertones.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'True',
        explanation:
          'Bluish veins suggest cool undertones. Greenish veins indicate warm undertones.',
        points: 10,
      },
    ],
  },

  {
    id: 'color-theory-quiz',
    moduleId: 'color-theory-fundamentals',
    title: 'Advanced Color Theory',
    passingScore: 75,
    timeLimit: 300,
    questions: [
      {
        id: 'ct-q1',
        question: 'What does "saturation" refer to in color?',
        type: 'multiple-choice',
        options: [
          'Lightness/darkness',
          'Color intensity/vibrancy',
          'The pure hue',
          'Color temperature',
        ],
        correctAnswer: 'Color intensity/vibrancy',
        explanation:
          'Saturation is how vivid or muted a color is. High saturation = vivid, low = muted.',
        points: 10,
      },
      {
        id: 'ct-q2',
        question: 'High contrast outfits (like white shirt + black tie) are typically:',
        type: 'multiple-choice',
        options: ['More casual', 'More formal and bold', 'Less noticeable', 'Weather-appropriate'],
        correctAnswer: 'More formal and bold',
        explanation:
          'High contrast creates bold, formal looks. Low contrast is more subtle and relaxed.',
        points: 10,
      },
      {
        id: 'ct-q3',
        question: 'Matching color temperatures within an outfit creates:',
        type: 'multiple-choice',
        options: ['Contrast', 'Cohesion', 'Confusion', 'Casualness'],
        correctAnswer: 'Cohesion',
        explanation:
          'Keeping colors at similar temperatures (all warm or all cool) creates visual harmony.',
        points: 10,
      },
    ],
  },

  {
    id: 'personal-color-quiz',
    moduleId: 'personal-color-analysis',
    title: 'Personal Color Analysis',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'pc-q1',
        question: 'What lighting is best for color self-assessment?',
        type: 'multiple-choice',
        options: [
          'Fluorescent light',
          'Natural daylight',
          'Warm incandescent',
          'Any lighting works',
        ],
        correctAnswer: 'Natural daylight',
        explanation:
          'Natural daylight shows true colors without the distortion of artificial lighting.',
        points: 10,
      },
      {
        id: 'pc-q2',
        question: 'How many colors should a personal palette include?',
        type: 'multiple-choice',
        options: ['5-8', '12-15', '20-25', '30+'],
        correctAnswer: '12-15',
        explanation: '12-15 colors provide versatility while maintaining cohesion.',
        points: 10,
      },
      {
        id: 'pc-q3',
        question: 'Wrong colors for your coloring can make you look:',
        type: 'multiple-choice',
        options: ['More energetic', 'Tired with emphasized shadows', 'Taller', 'More professional'],
        correctAnswer: 'Tired with emphasized shadows',
        explanation:
          'Wrong colors can emphasize dark circles, make skin look sallow, and create an overall tired appearance.',
        points: 10,
      },
    ],
  },

  {
    id: 'color-combinations-quiz',
    moduleId: 'color-combinations',
    title: 'Color Combinations',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'cc-q1',
        question: 'Complementary colors are located where on the color wheel?',
        type: 'multiple-choice',
        options: [
          'Next to each other',
          'Opposite each other',
          'Three steps apart',
          'In the same family',
        ],
        correctAnswer: 'Opposite each other',
        explanation:
          'Complementary colors sit directly opposite on the color wheel (blue-orange, red-green).',
        points: 10,
      },
      {
        id: 'cc-q2',
        question: 'What ratio is recommended for complementary color outfits?',
        type: 'multiple-choice',
        options: ['50-50', '70-30', '90-10', 'Equal parts'],
        correctAnswer: '70-30',
        explanation:
          'A 70-30 ratio prevents the two colors from competing and creates visual balance.',
        points: 10,
      },
      {
        id: 'cc-q3',
        question: 'Analogous colors create what type of look?',
        type: 'multiple-choice',
        options: [
          'Bold and contrasting',
          'Cohesive and sophisticated',
          'Random and eclectic',
          'Formal only',
        ],
        correctAnswer: 'Cohesive and sophisticated',
        explanation:
          'Analogous colors (adjacent on the wheel) create harmonious, subtle combinations.',
        points: 10,
      },
    ],
  },

  {
    id: 'seasonal-palettes-quiz',
    moduleId: 'seasonal-color-palettes',
    title: 'Seasonal Color Palettes',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'sp-q1',
        question: 'Which colors are characteristic of the Spring palette?',
        type: 'multiple-choice',
        options: [
          'Dusty rose, lavender',
          'Coral, peach, golden yellow',
          'Terracotta, olive, rust',
          'True red, emerald, black',
        ],
        correctAnswer: 'Coral, peach, golden yellow',
        explanation:
          'Spring colors are clear, warm, and bright like coral, peach, and golden yellow.',
        points: 10,
      },
      {
        id: 'sp-q2',
        question: 'Summer palette colors are typically:',
        type: 'multiple-choice',
        options: ['Warm and bright', 'Cool and muted', 'Warm and muted', 'Cool and bright'],
        correctAnswer: 'Cool and muted',
        explanation:
          'Summer is characterized by cool, soft, muted colors like powder blue and lavender.',
        points: 10,
      },
      {
        id: 'sp-q3',
        question: 'Autumn palette people should avoid which type of colors?',
        type: 'multiple-choice',
        options: ['Earthy tones', 'Cool and bright colors', 'Warm metals', 'Muted shades'],
        correctAnswer: 'Cool and bright colors',
        explanation:
          'Autumn colors are warm and muted. Cool and bright colors clash with Autumn coloring.',
        points: 10,
      },
    ],
  },

  {
    id: 'color-psychology-quiz',
    moduleId: 'color-psychology',
    title: 'Color Psychology',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'cp-q1',
        question: 'What does blue typically communicate?',
        type: 'multiple-choice',
        options: [
          'Power and passion',
          'Trust and competence',
          'Creativity and fun',
          'Wealth and luxury',
        ],
        correctAnswer: 'Trust and competence',
        explanation: 'Blue conveys trust, reliability, and calm professionalism.',
        points: 10,
      },
      {
        id: 'cp-q2',
        question: 'For a job interview, what color suit is generally recommended?',
        type: 'multiple-choice',
        options: ['Bright red', 'Navy', 'White', 'Yellow'],
        correctAnswer: 'Navy',
        explanation: 'Navy communicates trustworthiness and professionalism, ideal for interviews.',
        points: 10,
      },
      {
        id: 'cp-q3',
        question: 'White means mourning in some Asian cultures.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'True',
        explanation: 'Color meanings vary by culture. White represents mourning in parts of Asia.',
        points: 10,
      },
    ],
  },

  {
    id: 'accessory-fundamentals-quiz',
    moduleId: 'accessory-fundamentals',
    title: 'Accessory Fundamentals',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'af-q1',
        question: 'The "rule of three" for accessories suggests:',
        type: 'multiple-choice',
        options: [
          'Wear at least 3 accessories',
          'Limit statement accessories to 3',
          'Match 3 metals',
          'Have 3 of each item',
        ],
        correctAnswer: 'Limit statement accessories to 3',
        explanation: 'Limiting to three statement accessories prevents a cluttered look.',
        points: 10,
      },
      {
        id: 'af-q2',
        question: 'Which are the essential first accessories to invest in?',
        type: 'multiple-choice',
        options: [
          'Cufflinks and tie bar',
          'Quality tie, matching belt, classic watch',
          'Lapel pins and pocket squares',
          'Multiple rings',
        ],
        correctAnswer: 'Quality tie, matching belt, classic watch',
        explanation:
          'These three fundamentals work for most occasions and should be your first investments.',
        points: 10,
      },
      {
        id: 'af-q3',
        question: 'Formal accessories include all EXCEPT:',
        type: 'multiple-choice',
        options: ['Silk tie', 'Metal cufflinks', 'Dress watch', 'Rubber sports watch'],
        correctAnswer: 'Rubber sports watch',
        explanation:
          'Rubber sports watches are casual. Formal settings call for dress watches with leather straps.',
        points: 10,
      },
    ],
  },

  {
    id: 'tie-styles-quiz',
    moduleId: 'tie-styles-guide',
    title: 'Tie Styles Guide',
    passingScore: 70,
    timeLimit: 300,
    questions: [
      {
        id: 'ts-q1',
        question: 'Which tie material is most formal?',
        type: 'multiple-choice',
        options: ['Knit', 'Cotton', 'Silk', 'Linen'],
        correctAnswer: 'Silk',
        explanation: 'Silk ties are smooth and lustrous, making them the most formal option.',
        points: 10,
      },
      {
        id: 'ts-q2',
        question: 'When building a tie collection, start with:',
        type: 'multiple-choice',
        options: ['Bold patterns', 'Navy and burgundy solids', 'Novelty ties', 'Bow ties only'],
        correctAnswer: 'Navy and burgundy solids',
        explanation: 'Solid ties in classic colors are the most versatile foundation.',
        points: 10,
      },
      {
        id: 'ts-q3',
        question: 'Knit ties are appropriate for:',
        type: 'multiple-choice',
        options: ['Black tie events', 'Smart casual settings', 'Funerals', 'Board meetings'],
        correctAnswer: 'Smart casual settings',
        explanation:
          'Knit ties have texture that makes them perfect for relaxed, smart casual looks.',
        points: 10,
      },
    ],
  },

  {
    id: 'watch-pairing-quiz',
    moduleId: 'watch-basics',
    title: 'Watch Pairing Mastery',
    passingScore: 80,
    timeLimit: 300,
    questions: [
      {
        id: 'wp-q1',
        question: 'What type of watch is most appropriate for formal events?',
        type: 'multiple-choice',
        options: [
          'Diver with steel bracelet',
          'Dress watch with leather strap',
          'Digital sports watch',
          'Smart watch',
        ],
        correctAnswer: 'Dress watch with leather strap',
        explanation:
          'Dress watches are thin with simple dials and leather straps, perfect for formal occasions.',
        points: 10,
      },
      {
        id: 'wp-q2',
        question: 'A "three-watch collection" should include all EXCEPT:',
        type: 'multiple-choice',
        options: [
          'Dress watch',
          'Versatile daily wear',
          'Casual/sport watch',
          'Multiple dress watches',
        ],
        correctAnswer: 'Multiple dress watches',
        explanation:
          'One dress, one versatile daily, and one casual/sport covers 95% of situations.',
        points: 10,
      },
      {
        id: 'wp-q3',
        question: 'Sapphire crystal is preferred because it is:',
        type: 'multiple-choice',
        options: ['Cheaper', 'Scratch resistant', 'Lighter weight', 'More colorful'],
        correctAnswer: 'Scratch resistant',
        explanation:
          'Sapphire crystal is highly scratch resistant, maintaining watch clarity over time.',
        points: 10,
      },
      {
        id: 'wp-q4',
        question: 'Which watch is appropriate with a business suit?',
        type: 'multiple-choice',
        options: [
          'Large diving watch with neon accents',
          'Dress watch or subtle chronograph',
          'Digital fitness tracker',
          'Rubber sports watch',
        ],
        correctAnswer: 'Dress watch or subtle chronograph',
        explanation:
          'Business attire calls for refined watches that complement rather than compete.',
        points: 10,
      },
    ],
  },

  {
    id: 'belt-shoe-quiz',
    moduleId: 'belt-shoe-coordination',
    title: 'Belt & Shoe Coordination',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'bs-q1',
        question: 'The classic belt-shoe rule states:',
        type: 'multiple-choice',
        options: [
          'Always wear black',
          'Belt and shoes should match in color',
          'Contrast is key',
          'Texture matters most',
        ],
        correctAnswer: 'Belt and shoes should match in color',
        explanation: 'Traditional rule: brown belt with brown shoes, black belt with black shoes.',
        points: 10,
      },
      {
        id: 'bs-q2',
        question: 'Which leather color is most formal?',
        type: 'multiple-choice',
        options: ['Tan', 'Dark brown', 'Black', 'Burgundy'],
        correctAnswer: 'Black',
        explanation: 'Black leather is the most formal, ideal for evening and black-tie events.',
        points: 10,
      },
      {
        id: 'bs-q3',
        question: 'When starting your leather collection, buy first:',
        type: 'multiple-choice',
        options: ['Tan and burgundy', 'Black and dark brown', 'Suede only', 'Patent leather'],
        correctAnswer: 'Black and dark brown',
        explanation: 'Black and dark brown are the most versatile essentials for any wardrobe.',
        points: 10,
      },
    ],
  },

  {
    id: 'pocket-square-quiz',
    moduleId: 'pocket-square-folding',
    title: 'Pocket Square Mastery',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'ps-q1',
        question: 'Which pocket square fold is most formal?',
        type: 'multiple-choice',
        options: ['Puff fold', 'Presidential (flat) fold', 'Four-point fold', 'Casual bunch'],
        correctAnswer: 'Presidential (flat) fold',
        explanation: 'The presidential fold (clean rectangle) is the most formal and conservative.',
        points: 10,
      },
      {
        id: 'ps-q2',
        question: 'Your pocket square should:',
        type: 'multiple-choice',
        options: [
          'Match your tie exactly',
          'Never match your tie exactly',
          'Be the same fabric as your tie',
          'Always be white',
        ],
        correctAnswer: 'Never match your tie exactly',
        explanation: 'Exact matching looks like a pre-made set. Complement or contrast instead.',
        points: 10,
      },
      {
        id: 'ps-q3',
        question: 'Which pocket square fabric is most versatile?',
        type: 'multiple-choice',
        options: ['Silk paisley', 'White linen', 'Wool plaid', 'Cotton print'],
        correctAnswer: 'White linen',
        explanation:
          'White linen works with everything and is appropriate for all formal occasions.',
        points: 10,
      },
      {
        id: 'ps-q4',
        question: 'Pocket squares are appropriate with:',
        type: 'multiple-choice',
        options: ['T-shirts', 'Suits and blazers', 'Sweaters only', 'Never appropriate'],
        correctAnswer: 'Suits and blazers',
        explanation:
          "Pocket squares complete suits and blazers but aren't for ultra-casual outfits.",
        points: 10,
      },
    ],
  },

  // ==========================================
  // LEVEL 4 QUIZZES - Wardrobe Strategy
  // ==========================================
  {
    id: 'capsule-wardrobe-quiz',
    moduleId: 'capsule-wardrobe-principles',
    title: 'Capsule Wardrobe Principles',
    passingScore: 70,
    timeLimit: 300,
    questions: [
      {
        id: 'cw4-q1',
        question: 'How many interchangeable pieces typically form a capsule wardrobe?',
        type: 'multiple-choice',
        options: ['10-15', '25-40', '50-75', '100+'],
        correctAnswer: '25-40',
        explanation:
          'A capsule wardrobe typically contains 25-40 interchangeable pieces that cover all daily needs.',
        points: 10,
      },
      {
        id: 'cw4-q2',
        question:
          'Each item in a capsule wardrobe should pair with at least how many other pieces?',
        type: 'multiple-choice',
        options: ['1', '2', '3', '5'],
        correctAnswer: '3',
        explanation:
          'Every capsule piece must pair with at least 3 others to justify its place and create sufficient outfit combinations.',
        points: 10,
      },
      {
        id: 'cw4-q3',
        question: 'What percentage of a capsule wardrobe should tops represent?',
        type: 'multiple-choice',
        options: ['15%', '20%', '30%', '50%'],
        correctAnswer: '30%',
        explanation:
          'Tops (shirts, tees, sweaters) should make up about 30% of a balanced capsule wardrobe.',
        points: 10,
      },
      {
        id: 'cw4-q4',
        question:
          'A warm-climate capsule wardrobe needs fewer outerwear pieces than a cold-climate one.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'True',
        explanation:
          'Climate directly affects category ratios. Warm climates need lighter tops and fewer outerwear pieces.',
        points: 10,
      },
      {
        id: 'cw4-q5',
        question: 'How many items should you aim for in your first capsule wardrobe?',
        type: 'multiple-choice',
        options: ['15', '20', '30', '50'],
        correctAnswer: '30',
        explanation:
          'Start with 30 items for your first capsule, then refine over 2-3 seasons based on what you actually wear.',
        points: 10,
      },
    ],
  },

  {
    id: 'versatile-pieces-quiz',
    moduleId: 'versatile-pieces',
    title: 'Identifying Versatile Pieces',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'vp-q1',
        question: 'What three axes should you use to rate garment versatility?',
        type: 'multiple-choice',
        options: [
          'Price, brand, trendiness',
          'Occasion range, season range, combination count',
          'Color, pattern, texture',
          'Fit, fabric, finish',
        ],
        correctAnswer: 'Occasion range, season range, combination count',
        explanation:
          'Versatility is measured by how many occasions, seasons, and outfit combinations a piece supports.',
        points: 15,
      },
      {
        id: 'vp-q2',
        question: 'Which trait is NOT characteristic of versatile wardrobe pieces?',
        type: 'multiple-choice',
        options: ['Neutral or muted colors', 'Bold logo branding', 'Clean lines', 'Moderate fit'],
        correctAnswer: 'Bold logo branding',
        explanation:
          'Minimal branding is a hallmark of versatile pieces. Bold logos limit outfit compatibility.',
        points: 10,
      },
      {
        id: 'vp-q3',
        question: 'What percentage of your core rotation should be top-tier versatile items?',
        type: 'multiple-choice',
        options: ['30%', '50%', '70%', '90%'],
        correctAnswer: '70%',
        explanation:
          'Aiming for 70% high-versatility items ensures maximum outfit possibilities from your core rotation.',
        points: 10,
      },
      {
        id: 'vp-q4',
        question:
          'A gray crewneck sweater is considered highly versatile because it works under blazers and over casual tees.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'True',
        explanation:
          'A well-fitted gray crewneck exemplifies versatility by working across both smart and casual contexts.',
        points: 10,
      },
    ],
  },

  {
    id: 'cost-per-wear-quiz',
    moduleId: 'cost-per-wear-thinking',
    title: 'Cost-Per-Wear Thinking',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'cpw-q1',
        question: 'What is the cost-per-wear of a $300 pair of shoes worn 200 times?',
        type: 'multiple-choice',
        options: ['$0.50', '$1.50', '$3.00', '$15.00'],
        correctAnswer: '$1.50',
        explanation: 'Cost-per-wear = $300 / 200 wears = $1.50 per wear.',
        points: 10,
      },
      {
        id: 'cpw-q2',
        question: 'Which wardrobe category generally has the lowest cost-per-wear?',
        type: 'multiple-choice',
        options: [
          'Trendy accessories',
          'Quality shoes and outerwear',
          'Seasonal items',
          'Fast-fashion basics',
        ],
        correctAnswer: 'Quality shoes and outerwear',
        explanation:
          'Shoes and outerwear are worn frequently and quality versions last years, producing the lowest cost-per-wear.',
        points: 10,
      },
      {
        id: 'cpw-q3',
        question: 'What is the "false economy" in wardrobe spending?',
        type: 'multiple-choice',
        options: [
          'Buying expensive items',
          'Buying cheap items that need frequent replacement',
          'Shopping only during sales',
          'Investing in classics',
        ],
        correctAnswer: 'Buying cheap items that need frequent replacement',
        explanation:
          'Cheap items that wear out quickly often cost more total than a single quality piece that lasts.',
        points: 15,
      },
      {
        id: 'cpw-q4',
        question: 'Your most-worn items deserve the highest investment.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'True',
        explanation:
          'Items you wear daily will have the lowest cost-per-wear, making quality investment most justified.',
        points: 10,
      },
    ],
  },

  {
    id: 'seasonal-rotation-quiz',
    moduleId: 'seasonal-rotation-strategy',
    title: 'Seasonal Rotation Strategy',
    passingScore: 70,
    timeLimit: 300,
    questions: [
      {
        id: 'sr-q1',
        question: 'What percentage of your wardrobe should be year-round core items?',
        type: 'multiple-choice',
        options: ['20%', '40%', '60%', '80%'],
        correctAnswer: '40%',
        explanation:
          'About 40% of your wardrobe should be year-round core pieces, with 60% being seasonal extensions.',
        points: 10,
      },
      {
        id: 'sr-q2',
        question: 'What are "bridge pieces" in seasonal dressing?',
        type: 'multiple-choice',
        options: [
          'Pieces that bridge formal and casual',
          'Items that ease transitions between seasons',
          'Clothes you wear while traveling',
          'Pieces that match any color',
        ],
        correctAnswer: 'Items that ease transitions between seasons',
        explanation:
          'Bridge pieces like lightweight merino sweaters work across season transitions.',
        points: 10,
      },
      {
        id: 'sr-q3',
        question: 'Off-season clothes should be stored in plastic garment bags.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'False',
        explanation:
          'Never use plastic bags for storage. They trap moisture and can cause mildew. Use breathable cotton garment bags.',
        points: 10,
      },
      {
        id: 'sr-q4',
        question: 'How often should you conduct a full wardrobe audit?',
        type: 'multiple-choice',
        options: ['Monthly', 'Every 6 months', 'Once a year', 'Every 3 years'],
        correctAnswer: 'Every 6 months',
        explanation:
          'A biannual audit before each major season change keeps your wardrobe efficient and identifies gaps.',
        points: 10,
      },
      {
        id: 'sr-q5',
        question: 'Before storing off-season items, you should:',
        type: 'multiple-choice',
        options: [
          'Fold them quickly',
          'Clean everything first',
          'Spray with cologne',
          'Leave them on hangers',
        ],
        correctAnswer: 'Clean everything first',
        explanation:
          'Always clean garments before seasonal storage. Stains that sit for months become permanent.',
        points: 10,
      },
    ],
  },

  {
    id: 'wardrobe-editing-quiz',
    moduleId: 'wardrobe-editing',
    title: 'Wardrobe Editing',
    passingScore: 70,
    timeLimit: 300,
    questions: [
      {
        id: 'we-q1',
        question:
          'In the Keep/Donate/Replace framework, how many "no" answers means the item should go?',
        type: 'multiple-choice',
        options: ['One', 'Two or more', 'Three', 'Any "no" means donate'],
        correctAnswer: 'Two or more',
        explanation:
          'Two or more "no" answers to the three key questions means the item should be donated or replaced.',
        points: 10,
      },
      {
        id: 'we-q2',
        question: '"I paid a lot for this" is an example of which thinking trap?',
        type: 'multiple-choice',
        options: ['Confirmation bias', 'Sunk cost fallacy', 'Anchoring', 'Loss aversion'],
        correctAnswer: 'Sunk cost fallacy',
        explanation:
          "What you paid is a sunk cost. The item's current value to your wardrobe is what matters.",
        points: 10,
      },
      {
        id: 'we-q3',
        question: 'What percentage of your wardrobe should you aim to remove in your first edit?',
        type: 'multiple-choice',
        options: ['5-10%', '20-30%', '40-50%', '60%+'],
        correctAnswer: '20-30%',
        explanation:
          'A first edit typically removes 20-30% of items, creating space for a more curated collection.',
        points: 10,
      },
      {
        id: 'we-q4',
        question: 'The "one in, one out" rule helps maintain wardrobe size over time.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'True',
        explanation: 'Removing one item for every purchase prevents gradual wardrobe bloat.',
        points: 10,
      },
    ],
  },

  // ==========================================
  // LEVEL 5 QUIZZES - Style Development
  // ==========================================
  {
    id: 'personal-style-quiz',
    moduleId: 'finding-personal-style',
    title: 'Finding Your Personal Style',
    passingScore: 70,
    timeLimit: 300,
    questions: [
      {
        id: 'fps-q1',
        question: 'What three dimensions should you assess when discovering personal style?',
        type: 'multiple-choice',
        options: [
          'Budget, trends, brands',
          'Daily activities, comfort zone, aspirations',
          'Height, weight, age',
          'Color, fit, fabric',
        ],
        correctAnswer: 'Daily activities, comfort zone, aspirations',
        explanation:
          'Personal style starts with what you actually do, what you naturally gravitate toward, and how you want to be perceived.',
        points: 15,
      },
      {
        id: 'fps-q2',
        question: 'How many style descriptor words should define your personal style?',
        type: 'multiple-choice',
        options: ['1-2', '3-5', '8-10', '15+'],
        correctAnswer: '3-5',
        explanation:
          'Distill your style into 3-5 descriptive words that serve as a filter for every purchase decision.',
        points: 10,
      },
      {
        id: 'fps-q3',
        question:
          'When analyzing your wardrobe for style patterns, you should focus on your least-worn items.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'False',
        explanation:
          'Focus on your most-worn items. They reveal your unconscious preferences and true style tendencies.',
        points: 10,
      },
      {
        id: 'fps-q4',
        question: 'A new purchase should match at least how many of your style descriptor words?',
        type: 'multiple-choice',
        options: ['1', '2', 'All of them', 'None, just go with instinct'],
        correctAnswer: '2',
        explanation:
          'If a piece matches at least 2 of your style words, it will integrate cohesively into your wardrobe.',
        points: 10,
      },
    ],
  },

  {
    id: 'style-icons-quiz',
    moduleId: 'style-icons-inspiration',
    title: 'Style Icons & Inspiration',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'si-q1',
        question: 'What is the most important trait style icons share?',
        type: 'multiple-choice',
        options: [
          'Expensive clothing',
          'Consistency in their look',
          'Following every trend',
          'Wearing designer labels',
        ],
        correctAnswer: 'Consistency in their look',
        explanation:
          'Style icons have recognizable, consistent looks that express their personality authentically.',
        points: 10,
      },
      {
        id: 'si-q2',
        question: 'When inspired by a specific outfit, you should:',
        type: 'multiple-choice',
        options: [
          'Copy it exactly',
          'Extract the underlying style principles',
          'Buy the same brands',
          'Ignore it and stay original',
        ],
        correctAnswer: 'Extract the underlying style principles',
        explanation:
          'Extract principles (color harmony, proportions, texture contrast) rather than copying specific pieces.',
        points: 15,
      },
      {
        id: 'si-q3',
        question: 'When adapting vintage era styles, you should keep silhouettes modern.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'True',
        explanation:
          'Keep silhouettes modern and mix era-inspired pieces with contemporary items to avoid costume territory.',
        points: 10,
      },
      {
        id: 'si-q4',
        question: 'How often should you revisit and prune your style inspiration collection?',
        type: 'multiple-choice',
        options: ['Weekly', 'Monthly', 'Yearly', 'Never'],
        correctAnswer: 'Monthly',
        explanation:
          'Monthly reviews keep your reference library current and aligned with your evolving style direction.',
        points: 10,
      },
    ],
  },

  {
    id: 'adapting-trends-quiz',
    moduleId: 'adapting-trends',
    title: 'Adapting Trends Appropriately',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'at-q1',
        question: 'How long does a typical fashion trend last?',
        type: 'multiple-choice',
        options: ['1-2 months', '1-2 seasons', '3-7 years', '10+ years'],
        correctAnswer: '3-7 years',
        explanation: 'Trends last 3-7 years, unlike fads (1-2 seasons) or classics (decades).',
        points: 10,
      },
      {
        id: 'at-q2',
        question: 'What is the recommended wardrobe ratio of classics to trends to personal flair?',
        type: 'multiple-choice',
        options: ['50-30-20', '70-20-10', '30-50-20', '40-40-20'],
        correctAnswer: '70-20-10',
        explanation:
          'Build your foundation on 70% classics, selectively adopt 20% trends, and express 10% personal flair.',
        points: 10,
      },
      {
        id: 'at-q3',
        question: 'The best way to test a trend is to invest heavily in a full outfit.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'False',
        explanation:
          'Adopt trends at the lowest-cost entry point first, like an accessory, before committing to larger pieces.',
        points: 10,
      },
      {
        id: 'at-q4',
        question: 'How many trend pieces should you limit yourself to per season?',
        type: 'multiple-choice',
        options: ['1', '2-3', '5-6', 'No limit'],
        correctAnswer: '2-3',
        explanation:
          'Limiting to 2-3 trend pieces per season prevents them from overwhelming your established wardrobe.',
        points: 10,
      },
    ],
  },

  {
    id: 'signature-looks-quiz',
    moduleId: 'building-signature-looks',
    title: 'Building Signature Looks',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'sl-q1',
        question: 'A signature look means wearing the exact same outfit every day.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'False',
        explanation:
          'A signature look is a consistent visual language with recurring elements, not the same outfit repeated.',
        points: 10,
      },
      {
        id: 'sl-q2',
        question: 'How many outfit formulas should you create for your main occasions?',
        type: 'multiple-choice',
        options: ['1', '2', '3-4', '8-10'],
        correctAnswer: '3-4',
        explanation:
          'Create 3-4 reliable formulas covering your main occasions (work, weekend, evening, etc.).',
        points: 10,
      },
      {
        id: 'sl-q3',
        question: 'What defines a "signature element" in your style?',
        type: 'multiple-choice',
        options: [
          'The most expensive item you own',
          'A consistent detail that threads through all your outfits',
          'A piece you only wear for special occasions',
          'Whatever is currently trending',
        ],
        correctAnswer: 'A consistent detail that threads through all your outfits',
        explanation:
          'Signature elements are recurring details (a particular watch, preferred material, or consistent color) that create visual continuity.',
        points: 15,
      },
      {
        id: 'sl-q4',
        question: 'How often should you reassess your outfit formulas?',
        type: 'multiple-choice',
        options: ['Weekly', 'Each season', 'Every 5 years', 'Never'],
        correctAnswer: 'Each season',
        explanation:
          'Seasonal reassessment keeps your signature fresh while maintaining core identity.',
        points: 10,
      },
    ],
  },

  // ==========================================
  // LEVEL 6 QUIZZES - Advanced Coordination
  // ==========================================
  {
    id: 'pattern-mixing-quiz',
    moduleId: 'pattern-mixing',
    title: 'Pattern Mixing Mastery',
    passingScore: 70,
    timeLimit: 300,
    questions: [
      {
        id: 'pm-q1',
        question: 'The fundamental rule of pattern mixing is:',
        type: 'multiple-choice',
        options: [
          'Match pattern types',
          'Vary pattern scales',
          'Use the same colors',
          'Stick to one pattern',
        ],
        correctAnswer: 'Vary pattern scales',
        explanation:
          'Different scales create visual hierarchy. Same-scale patterns compete and clash.',
        points: 10,
      },
      {
        id: 'pm-q2',
        question: 'Which pattern combination is considered classic and foolproof?',
        type: 'multiple-choice',
        options: ['Stripes + stripes', 'Checks + checks', 'Stripes + dots', 'Plaid + plaid'],
        correctAnswer: 'Stripes + dots',
        explanation:
          'Stripes and dots are different pattern types that naturally complement each other at any scale.',
        points: 10,
      },
      {
        id: 'pm-q3',
        question:
          'Successful pattern mixing requires at least one shared color between patterned pieces.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'True',
        explanation:
          'A shared color thread connects patterned pieces. Without it, even correctly scaled patterns feel disjointed.',
        points: 10,
      },
      {
        id: 'pm-q4',
        question:
          'What is the maximum number of patterns recommended for most professional settings?',
        type: 'multiple-choice',
        options: ['One', 'Two', 'Three', 'Four'],
        correctAnswer: 'Two',
        explanation:
          'In professional settings, limit to two patterns maximum. Save three-pattern outfits for social contexts.',
        points: 10,
      },
      {
        id: 'pm-q5',
        question: 'When starting to mix patterns, begin with:',
        type: 'multiple-choice',
        options: [
          'Three bold patterns',
          'Two patterned pieces and one solid',
          'All patterns, no solids',
          'Only micro-patterns',
        ],
        correctAnswer: 'Two patterned pieces and one solid',
        explanation:
          'Start with two patterns anchored by a solid piece before graduating to three-pattern combinations.',
        points: 10,
      },
    ],
  },

  {
    id: 'texture-play-quiz',
    moduleId: 'texture-play',
    title: 'Texture Play',
    passingScore: 70,
    timeLimit: 240,
    questions: [
      {
        id: 'tp-q1',
        question: 'On the texture spectrum, which fabrics read most formal?',
        type: 'multiple-choice',
        options: [
          'Tweed and flannel',
          'Silk and polished leather',
          'Linen and cotton',
          'Corduroy and suede',
        ],
        correctAnswer: 'Silk and polished leather',
        explanation:
          'Smooth, shiny fabrics like silk and polished leather are the most formal end of the texture spectrum.',
        points: 10,
      },
      {
        id: 'tp-q2',
        question: 'How many distinct textures should an interesting outfit contain?',
        type: 'multiple-choice',
        options: ['1', '2-3', '5-6', 'As many as possible'],
        correctAnswer: '2-3',
        explanation: 'Mixing 2-3 textures creates visual interest without overwhelming the outfit.',
        points: 10,
      },
      {
        id: 'tp-q3',
        question: 'Suede shoes dress down an outfit by approximately one formality level.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'True',
        explanation:
          'Suede has a matte, soft texture that is inherently more casual than smooth polished leather.',
        points: 10,
      },
      {
        id: 'tp-q4',
        question: 'Texture contrast is especially powerful in which type of outfit?',
        type: 'multiple-choice',
        options: [
          'Multi-colored outfits',
          'Monochromatic outfits',
          'Patterned outfits',
          'Formal suits only',
        ],
        correctAnswer: 'Monochromatic outfits',
        explanation:
          'When color contrast is absent in monochromatic outfits, texture contrast becomes the primary source of visual interest.',
        points: 10,
      },
    ],
  },

  {
    id: 'proportions-quiz',
    moduleId: 'proportions-silhouette',
    title: 'Proportions & Silhouette',
    passingScore: 70,
    timeLimit: 300,
    questions: [
      {
        id: 'prop-q1',
        question: 'The "rule of thirds" in outfit proportions suggests:',
        type: 'multiple-choice',
        options: [
          'Wear three colors',
          'Divide the outfit visually into thirds, not halves',
          'Own three of each item',
          'Layer three pieces',
        ],
        correctAnswer: 'Divide the outfit visually into thirds, not halves',
        explanation:
          'A 1/3 to 2/3 visual split is more dynamic and flattering than a 50/50 divide.',
        points: 10,
      },
      {
        id: 'prop-q2',
        question: 'Structured garments with padded shoulders work best on:',
        type: 'multiple-choice',
        options: [
          'Already-athletic builds',
          'Slim frames that need definition',
          'All body types equally',
          'Only tall men',
        ],
        correctAnswer: 'Slim frames that need definition',
        explanation:
          'Structured garments add definition to slim frames. Athletic builds benefit more from unstructured pieces.',
        points: 10,
      },
      {
        id: 'prop-q3',
        question: 'Low-rise trousers visually lengthen the leg line.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'False',
        explanation:
          'Low-rise trousers shorten the visual leg line. Higher-rise trousers create the appearance of longer legs.',
        points: 10,
      },
      {
        id: 'prop-q4',
        question: 'What should you always prioritize when fitting a jacket?',
        type: 'multiple-choice',
        options: ['Sleeve length', 'Shoulder fit', 'Button placement', 'Pocket position'],
        correctAnswer: 'Shoulder fit',
        explanation:
          'Shoulder fit is the hardest to alter and most important for proper proportions. Other elements can be adjusted.',
        points: 15,
      },
    ],
  },

  {
    id: 'advanced-color-quiz',
    moduleId: 'advanced-color-coordination',
    title: 'Advanced Color Coordination',
    passingScore: 70,
    timeLimit: 360,
    questions: [
      {
        id: 'ac-q1',
        question: 'A split-complementary scheme uses:',
        type: 'multiple-choice',
        options: [
          'Two opposite colors',
          'One base color and the two colors adjacent to its complement',
          'Three equally spaced colors',
          'Four colors in a square',
        ],
        correctAnswer: 'One base color and the two colors adjacent to its complement',
        explanation:
          'Split-complementary takes one base and the two neighbors of its complement for a rich but balanced palette.',
        points: 15,
      },
      {
        id: 'ac-q2',
        question:
          'When using multiple colors, what percentage should the dominant color represent?',
        type: 'multiple-choice',
        options: ['30%', '40%', '50%', '60% or more'],
        correctAnswer: '60% or more',
        explanation:
          'The dominant color should be 60% or more to anchor the outfit and prevent visual competition.',
        points: 10,
      },
      {
        id: 'ac-q3',
        question: 'If all colors in an outfit are equally vivid, the result feels:',
        type: 'multiple-choice',
        options: ['Sophisticated', 'Costumey', 'Minimalist', 'Professional'],
        correctAnswer: 'Costumey',
        explanation:
          'Equal vividity creates competition. Vary saturation so one color leads and others support.',
        points: 10,
      },
      {
        id: 'ac-q4',
        question:
          'Advanced color coordination means every outfit element should connect to at least one other piece by color.',
        type: 'true-false',
        options: ['True', 'False'],
        correctAnswer: 'True',
        explanation:
          'In advanced coordination, every piece connects to at least one other through shared color, creating a cohesive story.',
        points: 10,
      },
      {
        id: 'ac-q5',
        question: 'The ultimate goal of studying color rules is to:',
        type: 'multiple-choice',
        options: [
          'Memorize every possible combination',
          'Always follow rules exactly',
          'Develop intuitive color sense that transcends rules',
          'Only wear neutral colors',
        ],
        correctAnswer: 'Develop intuitive color sense that transcends rules',
        explanation:
          'Color rules are training wheels. The goal is developing intuition through practice.',
        points: 10,
      },
    ],
  },
]

/**
 * Get module by ID
 */
export function getModuleById(id: string): EducationalModule | undefined {
  return EDUCATIONAL_MODULES.find((module) => module.id === id)
}

/**
 * Get modules by level
 */
export function getModulesByLevel(level: number): EducationalModule[] {
  return EDUCATIONAL_MODULES.filter((module) => module.level === level).sort(
    (a, b) => a.order - b.order
  )
}

/**
 * Get modules by category
 */
export function getModulesByCategory(category: string): EducationalModule[] {
  return EDUCATIONAL_MODULES.filter((module) => module.category === category).sort(
    (a, b) => a.order - b.order
  )
}

/**
 * Check if module is unlocked based on prerequisites
 */
export function isModuleUnlocked(moduleId: string, completedModuleIds: string[]): boolean {
  const module = getModuleById(moduleId)
  if (!module) return false

  // If no prerequisites, module is unlocked
  if (module.prerequisites.length === 0) return true

  // Check if all prerequisites are completed
  return module.prerequisites.every((prereqId) => completedModuleIds.includes(prereqId))
}

/**
 * Get quiz by ID
 */
export function getQuizById(id: string): Quiz | undefined {
  return QUIZZES.find((quiz) => quiz.id === id)
}

/**
 * Get quiz for module
 */
export function getQuizForModule(moduleId: string): Quiz | undefined {
  return QUIZZES.find((quiz) => quiz.moduleId === moduleId)
}

/**
 * Calculate quiz score
 */
export function calculateQuizScore(
  quiz: Quiz,
  answers: Record<string, string>
): {
  score: number
  maxScore: number
  percentage: number
  passed: boolean
  results: Array<{
    questionId: string
    correct: boolean
    earnedPoints: number
  }>
} {
  let earnedPoints = 0
  const results: Array<{ questionId: string; correct: boolean; earnedPoints: number }> = []

  for (const question of quiz.questions) {
    const userAnswer = answers[question.id]
    let correct = false

    if (question.type === 'multiple-choice' && question.correctAnswer.includes(',')) {
      // Multiple correct answers - check if user provided all correct answers
      const correctAnswers = question.correctAnswer.split(',').sort()
      const userAnswers = (userAnswer || '').split(',').sort()
      correct = JSON.stringify(correctAnswers) === JSON.stringify(userAnswers)
    } else {
      // Single correct answer
      correct = userAnswer === question.correctAnswer
    }

    const points = correct ? question.points : 0
    earnedPoints += points

    results.push({
      questionId: question.id,
      correct,
      earnedPoints: points,
    })
  }

  const maxScore = quiz.questions.reduce((sum, q) => sum + q.points, 0)
  const percentage = Math.round((earnedPoints / maxScore) * 100)
  const passed = percentage >= quiz.passingScore

  return {
    score: earnedPoints,
    maxScore,
    percentage,
    passed,
    results,
  }
}

/**
 * Get all available modules for user's level
 */
export function getAvailableModules(
  userLevel: number,
  completedModuleIds: string[]
): EducationalModule[] {
  return EDUCATIONAL_MODULES.filter(
    (module) => module.level <= userLevel && isModuleUnlocked(module.id, completedModuleIds)
  ).sort((a, b) => {
    // Sort by level first, then by order
    if (a.level !== b.level) return a.level - b.level
    return a.order - b.order
  })
}

/**
 * Calculate total XP from completed modules
 */
export function getTotalModuleXP(completedModuleIds: string[]): number {
  return EDUCATIONAL_MODULES.filter((m) => completedModuleIds.includes(m.id)).reduce(
    (sum, m) => sum + m.xpReward,
    0
  )
}

/**
 * Get module completion percentage
 */
export function getModuleCompletionPercentage(completedModuleIds: string[]): number {
  const total = EDUCATIONAL_MODULES.length
  const completed = completedModuleIds.length
  return Math.floor((completed / total) * 100)
}
